import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import { Link as RouterLink } from 'react-router-dom';

import {
  Alert,
  Avatar,
  Button,
  CircularProgress,
  Icon,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@/ui';

import { FeedbackModal } from './FeedbackModal';
import { ReflectionBlockActions } from './ReflectionBlockActions';
import { ReflectionOnboardingCallout } from './ReflectionOnboardingCallout';
import { ReflectionPromptReview } from './ReflectionPromptReview';
import {
  projectReflection,
  REFLECTION_ANCHOR_CONTRACT,
  REFLECTION_READER_CONTRACT,
  SAVED_NOTE_UNLOCATED,
  verifyReflectionAnchor,
} from './reflectionKeepProjection';
import { readReflectionKeepSelection } from './reflectionKeepSelection';
import { hasSupportedReflectionContracts } from './reflectionNotePlacement';
import { buildReflectionPrompt } from './reflectionPrompt';
import {
  blockNum,
  type ContentBlock,
  type HighlightNote,
  type MarkdownSegment,
  parseMarkdownBlocks,
  parseToVisibleSegments,
  resolveHighlightBounds,
} from './reflectionUtils';
import {
  type ArtifactSelectionButtonPosition,
  type ArtifactTextSelection,
  useArtifactTextSelection,
} from './useArtifactTextSelection';

import type { MomentsReflectionStoredSourceIdentity } from '@/store/api/momentsApi';
import type {
  ComponentProps,
  CSSProperties,
  MouseEvent,
  ReactNode,
} from 'react';

import theme from '@/ui/theme';

export type MemberReflectionFeedbackValue = 'positive' | 'negative' | null;
export type MemberReflectionCopyState = 'idle' | 'copied' | 'error';
export type MemberReflectionStatus =
  'loading' | 'ready' | 'not-found' | 'error';

/**
 * Display-only identity for private note cards. These notes are only ever
 * rendered for the member viewing their own reflection, so the single possible
 * author is the current user -- no author data is stored on the note itself.
 */
export interface MemberReflectionNoteAuthor {
  avatarUrl?: string;
  firstName?: string;
  lastName?: string;
}

export interface MemberReflectionArtifactHighlight extends HighlightNote {
  createdAt?: string;
  id: string;
  note?: string;
  selectedText?: string;
  updatedAt?: string;
  sourceIdentity?: MomentsReflectionStoredSourceIdentity | null;
}

const EMPTY_ARTIFACT_HIGHLIGHTS: MemberReflectionArtifactHighlight[] = [];

const RouterButton = Button as unknown as (
  props: ComponentProps<typeof Button> & {
    component: typeof RouterLink;
    to: string;
  },
) => ReactNode;

type ArtifactNoteInteractionState =
  | { type: 'idle' }
  | { draft: string; selection: ArtifactTextSelection; type: 'add' }
  | { activeNoteId: string; isMenuOpen: boolean; type: 'view' }
  | { activeNoteId: string; draft: string; type: 'edit' };

export interface MemberReflectionArtifactViewProps {
  artifactHighlights?: MemberReflectionArtifactHighlight[];
  artifactNoteAuthor?: MemberReflectionNoteAuthor;
  artifactNotesEnabled?: boolean;
  reflectionKeep?: { enabled: boolean; revision?: number };
  onManageSavedNotes?: () => void;
  artifactTitle?: string;
  coachSelectionCard?: {
    coachFirstName: string;
    href: string;
  };
  copyState?: MemberReflectionCopyState;
  contentUnavailableMessage?: string;
  copyAriaLabel?: string;
  copyTooltipLabel?: string;
  errorAction?: { href: string; label: string };
  errorMessage?: string;
  introDismissError?: string;
  feedbackValue?: MemberReflectionFeedbackValue;
  generationDateLabel?: string;
  introCopy?: string;
  isArtifactNoteDeleting?: boolean;
  isArtifactNoteUpdating?: boolean;
  isDismissingIntro?: boolean;
  isFeedbackSubmitting?: boolean;
  markdown?: string;
  onArtifactNoteCreate?: (
    selection: ArtifactTextSelection,
    note: string,
  ) =>
    | MemberReflectionArtifactHighlight
    | Promise<MemberReflectionArtifactHighlight | void>
    | void;
  onArtifactNoteDelete?: (id: string) => Promise<void> | void;
  onArtifactNoteUpdate?: (id: string, note: string) => Promise<void> | void;
  onArtifactTextSelection?: (selection: ArtifactTextSelection) => void;
  onBack?: () => void;
  onDismissIntro?: () => void;
  onCopy?: () => void;
  onFeedback?: (
    value: Exclude<MemberReflectionFeedbackValue, null>,
    feedbackText: string,
  ) => Promise<void>;
  participantLabel?: string;
  sessionDateLabel?: string;
  showIntroCallout?: boolean;
  showSavedNotesLink?: boolean;
  sourceReflectionLink?: {
    href: string;
    label: string;
  };
  status: MemberReflectionStatus;
  // Rendered below the transcript link once the artifact content has loaded.
  survey?: ReactNode;
  statusCopy?: {
    error?: string;
    loading?: string;
    notFound?: string;
  };
  transcriptHref?: string | null;
  viewerContext?: 'coach' | 'member';
}

const coachSelectionCardStyles: CSSProperties = {
  backgroundColor: '#FFFFFF',
  border: '1px solid #E3E3E3',
  borderRadius: 16,
  boxShadow:
    '0 3px 3px -2px rgba(0, 0, 0, 0.1), 0 3px 4px rgba(0, 0, 0, 0.1), 0 1px 8px rgba(0, 0, 0, 0.1)',
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
  marginTop: 24,
  padding: 32,
};

const COACH_INTRO_COPY =
  'This coaching reflection highlights session patterns, moments of insight, and opportunities to support follow-through. Review it before or after your next conversation to reconnect with what emerged.';

const font = {
  sans: "'Basis Grotesque', sans-serif",
  serif: "'Basis Grotesque', sans-serif",
};

const colors = {
  body: '#262626',
  caption: theme.extendedColors.cement[300],
  divider: '#E3E3E3',
  heading: '#262626',
  highlightActiveBg: '#FDE288',
  highlightBg: '#FFEBB2',
  muted: '#5C5C5C',
  pageBg: '#FEFBF7',
  sage: '#637A6F',
};

const contentStyles: CSSProperties = {
  maxWidth: 672,
  width: '100%',
};

const pageStyles: CSSProperties = {
  background: colors.pageBg,
  color: colors.body,
  minHeight: '100vh',
};

const desktopShellStyles: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  padding: '38px 24px 80px',
};

const desktopNotesShellStyles: CSSProperties = {
  alignItems: 'flex-start',
  display: 'grid',
  gap: 32,
  gridTemplateColumns: 'minmax(0, 672px) 320px',
  justifyContent: 'center',
  padding: '38px 24px 80px',
};

const desktopNotesColumnStyles: CSSProperties = {
  minHeight: 1,
  position: 'relative',
  width: 320,
};

const mobileShellStyles: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  padding: '26px 19px 80px',
};

const topBarStyles: CSSProperties = {
  alignItems: 'center',
  background: colors.pageBg,
  borderBottom: `1px solid ${colors.divider}`,
  boxSizing: 'border-box',
  display: 'flex',
  height: 72,
  justifyContent: 'space-between',
  padding: '24px',
  width: '100%',
};

const topBarLeftStyles: CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  gap: 8,
  minWidth: 0,
};

const desktopHeaderLeftStyles: CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  gap: 8,
  minWidth: 0,
};

const mobileHeaderMetaStyles: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  padding: '28px 28px 0',
};

const headerTitleStyles: CSSProperties = {
  color: colors.sage,
  fontFamily: font.sans,
  fontSize: 16,
  fontWeight: 700,
  letterSpacing: 0.15,
  lineHeight: 1.5,
  margin: 0,
};

const headerSessionStyles: CSSProperties = {
  color: colors.body,
  fontFamily: font.sans,
  fontSize: 16,
  fontWeight: 700,
  letterSpacing: 0.15,
  lineHeight: 1.5,
  margin: 0,
};

const headerGeneratedStyles: CSSProperties = {
  color: colors.body,
  fontFamily: font.sans,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1.334,
  margin: 0,
};

const headerDividerStyles: CSSProperties = {
  color: colors.body,
  fontFamily: font.sans,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1.334,
};

const calloutStyles: CSSProperties = {
  background: colors.pageBg,
  border: `1px solid ${colors.divider}`,
  borderRadius: 16,
  margin: '0 0 32px',
  padding: '16px 18px',
};

const transcriptFooterStyles: CSSProperties = {
  borderTop: `1px solid ${colors.divider}`,
  marginTop: 24,
  paddingTop: 16,
};

const transcriptLinkStyles: CSSProperties = {
  color: colors.sage,
  fontFamily: font.sans,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1.334,
  textDecoration: 'none',
};

const actionBarStyles: CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  gap: 24,
};

const selectionButtonHeight = 34;
const selectionButtonTopMargin = 8;

// The desktop selection button sits beside the start of the highlight. It
// holds at the top edge while part of the highlight is still visible, and
// moves fully off-screen with the highlight once the highlight is gone.
const getSelectionButtonTop = ({
  bottom,
  top,
}: {
  bottom: number;
  top: number;
}): number =>
  bottom <= 0
    ? bottom - selectionButtonHeight
    : Math.max(
        top,
        Math.min(selectionButtonTopMargin, bottom - selectionButtonHeight),
      );

const selectionButtonStyles: CSSProperties = {
  alignItems: 'center',
  background: '#FFFFFF',
  border: `1px solid ${colors.divider}`,
  borderRadius: 8,
  boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
  color: colors.body,
  cursor: 'pointer',
  display: 'flex',
  fontFamily: font.sans,
  fontSize: 13,
  fontWeight: 600,
  height: selectionButtonHeight,
  justifyContent: 'center',
  padding: '0 12px',
  position: 'fixed',
  whiteSpace: 'nowrap',
  width: 96,
  zIndex: 80,
};

const mobileSelectionButtonStyles: CSSProperties = {
  ...selectionButtonStyles,
  bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
  boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
  fontSize: 15,
  height: 42,
  left: 18,
  right: 18,
  top: undefined,
  width: 'auto',
  zIndex: 95,
};

const mobilePendingNoteDockStyles: CSSProperties = {
  background: '#FFFFFF',
  borderTop: `1px solid ${colors.divider}`,
  bottom: 0,
  boxShadow: '0 -6px 20px rgba(0,0,0,0.12)',
  boxSizing: 'border-box',
  left: 0,
  maxWidth: '100vw',
  overflowX: 'hidden',
  padding: '16px 16px calc(env(safe-area-inset-bottom, 0px) + 16px)',
  position: 'fixed',
  right: 'auto',
  width: '100vw',
  zIndex: 95,
};

const mobileNoteViewerStyles: CSSProperties = {
  background: '#FFFFFF',
  borderTop: `1px solid ${colors.divider}`,
  bottom: 0,
  boxShadow: '0 -6px 20px rgba(0,0,0,0.14)',
  boxSizing: 'border-box',
  left: 0,
  minHeight: 276,
  padding: '0 24px calc(env(safe-area-inset-bottom, 0px) + 24px)',
  position: 'fixed',
  right: 0,
  zIndex: 90,
};

const mobileNoteViewerToolbarStyles: CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  height: 56,
  justifyContent: 'space-between',
};

const pendingNoteStyles: CSSProperties = {
  background: '#FFFFFF',
  border: `1px solid ${colors.divider}`,
  borderRadius: 12,
  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  boxSizing: 'border-box',
  margin: '24px 0',
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  padding: 16,
  width: '100%',
};

const textareaStyles: CSSProperties = {
  border: `1px solid ${colors.divider}`,
  borderRadius: 8,
  boxSizing: 'border-box',
  color: colors.body,
  fontFamily: font.sans,
  fontSize: 14,
  lineHeight: 1.5,
  minHeight: 96,
  padding: 12,
  resize: 'vertical',
  width: '100%',
};

const noteActionsStyles: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  justifyContent: 'flex-end',
  marginTop: 12,
};

const mobileComposerShellStyles: CSSProperties = {
  background: 'transparent',
  border: 0,
  borderRadius: 0,
  boxShadow: 'none',
  margin: 0,
  padding: 0,
};

const mobileComposerInputRowStyles: CSSProperties = {
  alignItems: 'flex-end',
  display: 'flex',
  gap: 10,
};

const mobileComposerTextareaStyles: CSSProperties = {
  ...textareaStyles,
  fontSize: 16,
  minHeight: 48,
  resize: 'none',
};

const noteCardStackGap = 16;
// MEN-4684 removed the repeated selected-text quote from saved cards, making
// them shorter than the previous 260px estimate. The hover-revealed "..."
// menu is an absolutely-positioned overlay (per Itai's Figma mock, node
// 18519:4409) rather than an in-flow row, so it doesn't add to card height.
const estimatedSavedNoteCardHeight = 180;
const estimatedPendingNoteCardHeight = 260;
const estimatedPendingNoteErrorHeight = 64;
const noteColumnBottomPadding = 32;

const noteButtonStyles: CSSProperties = {
  border: `1px solid ${colors.divider}`,
  borderRadius: 8,
  cursor: 'pointer',
  fontFamily: font.sans,
  fontSize: 13,
  fontWeight: 600,
  height: 34,
  padding: '0 12px',
};

const primaryNoteButtonStyles: CSSProperties = {
  ...noteButtonStyles,
  background: colors.sage,
  border: `1px solid ${colors.sage}`,
  color: '#FFFFFF',
};

const secondaryNoteButtonStyles: CSSProperties = {
  ...noteButtonStyles,
  background: '#FFFFFF',
  color: colors.body,
};

const applyFormat = (
  type: MarkdownSegment['type'],
  text: string,
  key: string,
): ReactNode => {
  if (type === 'bold') {
    return (
      <strong key={key} style={{ color: 'inherit', fontWeight: 600 }}>
        {text}
      </strong>
    );
  }
  if (type === 'italic') {
    return <em key={key}>{text}</em>;
  }
  return <span key={key}>{text}</span>;
};

const highlightMarkStyles: CSSProperties = {
  background: colors.highlightBg,
  borderRadius: 3,
  color: 'inherit',
  fontStyle: 'inherit',
  fontWeight: 'inherit',
  padding: '1px 0',
  transition: 'background-color 180ms ease, box-shadow 180ms ease',
};

const getHighlightMarkStyles = (isActive: boolean): CSSProperties => ({
  ...highlightMarkStyles,
  background: isActive ? colors.highlightActiveBg : colors.highlightBg,
  boxShadow: isActive ? `0 0 0 3px ${colors.highlightActiveBg}` : undefined,
});

const BlockText = ({
  activeHighlightId,
  block,
  highlights,
  onHighlightClick,
}: {
  activeHighlightId?: string | null;
  block: ContentBlock;
  highlights: MemberReflectionArtifactHighlight[];
  onHighlightClick?: (highlightId: string) => void;
}) => {
  const segments = parseToVisibleSegments(block.text);
  const blockHighlightBounds = highlights
    .map((highlight) => ({
      bounds: resolveHighlightBounds(highlight, block.id),
      highlight,
    }))
    .filter(
      (
        entry,
      ): entry is {
        bounds: { endIdx: number; startIdx: number };
        highlight: MemberReflectionArtifactHighlight;
      } => entry.bounds !== null,
    );

  if (blockHighlightBounds.length === 0) {
    return (
      <>
        {segments.map((segment, index) =>
          applyFormat(segment.type, segment.text, `${block.id}-${index}`),
        )}
      </>
    );
  }

  const parts: ReactNode[] = [];

  for (const segment of segments) {
    const boundaries = new Set([segment.vStart, segment.vEnd]);
    for (const { bounds } of blockHighlightBounds) {
      const overlapStart = Math.max(bounds.startIdx, segment.vStart);
      const overlapEnd = Math.min(bounds.endIdx, segment.vEnd);
      if (overlapStart < overlapEnd) {
        boundaries.add(overlapStart);
        boundaries.add(overlapEnd);
      }
    }

    const sortedBoundaries = [...boundaries].sort((a, b) => a - b);
    for (let index = 0; index < sortedBoundaries.length - 1; index += 1) {
      const partStart = sortedBoundaries[index] ?? segment.vStart;
      const partEnd = sortedBoundaries[index + 1] ?? segment.vEnd;
      if (partStart < partEnd) {
        const text = segment.text.slice(
          partStart - segment.vStart,
          partEnd - segment.vStart,
        );
        const activeHighlights = blockHighlightBounds
          .filter(
            ({ bounds }) =>
              bounds.startIdx <= partStart && bounds.endIdx >= partEnd,
          )
          .sort((a, b) => {
            const aLength = a.bounds.endIdx - a.bounds.startIdx;
            const bLength = b.bounds.endIdx - b.bounds.startIdx;
            if (aLength !== bLength) {
              return bLength - aLength;
            }
            return a.highlight.id.localeCompare(b.highlight.id);
          });

        let node = applyFormat(
          segment.type,
          text,
          `${block.id}-${segment.vStart}-${partStart}-text`,
        );
        if (onHighlightClick && activeHighlights.length > 0) {
          const targetHighlight =
            activeHighlights[activeHighlights.length - 1]?.highlight;
          if (targetHighlight) {
            const isActive = activeHighlights.some(
              ({ highlight }) => highlight.id === activeHighlightId,
            );
            node = (
              // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- Mobile highlights must remain selectable text; native buttons disrupt iOS text selection.
              <span
                key={`${block.id}-${segment.vStart}-${partStart}-highlight-${targetHighlight.id}`}
                data-highlight-active={isActive ? 'true' : undefined}
                data-highlight-id={targetHighlight.id}
                style={{
                  ...getHighlightMarkStyles(isActive),
                  WebkitUserSelect: 'text',
                  cursor: 'pointer',
                  userSelect: 'text',
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  onHighlightClick(targetHighlight.id);
                }}
              >
                {node}
              </span>
            );
          }
        } else {
          for (const { highlight } of activeHighlights) {
            const highlightKey = `${block.id}-${segment.vStart}-${partStart}-highlight-${highlight.id}`;
            const highlightStyles = getHighlightMarkStyles(
              activeHighlightId === highlight.id,
            );
            node = (
              <span
                key={highlightKey}
                data-highlight-active={
                  activeHighlightId === highlight.id ? 'true' : undefined
                }
                data-highlight-id={highlight.id}
                style={highlightStyles}
              >
                {node}
              </span>
            );
          }
        }
        parts.push(node);
      }
    }
  }

  return parts;
};

const ReflectionContent = ({
  activeHighlightId,
  highlights,
  markdown,
  onHighlightClick,
  onKeep,
  onUse,
}: {
  activeHighlightId?: string | null;
  highlights: MemberReflectionArtifactHighlight[];
  markdown: string;
  onHighlightClick?: (highlightId: string) => void;
  onKeep?: (block: ContentBlock) => void;
  onUse?: (block: ContentBlock) => void;
}) => {
  const blocks = parseMarkdownBlocks(markdown);
  const renderBlock = (block: ContentBlock) => {
    if (block.type === 'heading') {
      const level = block.level ?? 2;
      const sizes: Record<number, number> = {
        1: 28,
        2: 22,
        3: 18,
        4: 16,
        5: 14,
        6: 13,
      };
      const headingStyle: CSSProperties = {
        color: colors.heading,
        fontFamily: font.serif,
        fontSize: sizes[level] ?? 18,
        fontWeight: 600,
        lineHeight: 1.3,
        margin: '32px 0 14px',
      };
      const headingInner = (
        <BlockText
          activeHighlightId={activeHighlightId}
          block={block}
          highlights={highlights}
          onHighlightClick={onHighlightClick}
        />
      );

      if (level === 1) {
        return (
          <h1 key={block.id} data-block-id={block.id} style={headingStyle}>
            {headingInner}
          </h1>
        );
      }
      if (level === 2) {
        return (
          <h2 key={block.id} data-block-id={block.id} style={headingStyle}>
            {headingInner}
          </h2>
        );
      }
      if (level === 3) {
        return (
          <h3 key={block.id} data-block-id={block.id} style={headingStyle}>
            {headingInner}
          </h3>
        );
      }
      if (level === 4) {
        return (
          <h4 key={block.id} data-block-id={block.id} style={headingStyle}>
            {headingInner}
          </h4>
        );
      }
      if (level === 5) {
        return (
          <h5 key={block.id} data-block-id={block.id} style={headingStyle}>
            {headingInner}
          </h5>
        );
      }
      return (
        <h6 key={block.id} data-block-id={block.id} style={headingStyle}>
          {headingInner}
        </h6>
      );
    }

    if (block.type === 'quote') {
      return (
        <blockquote
          key={block.id}
          style={{
            borderLeft: '2px solid #d4c9b8',
            margin: '16px 0',
            paddingLeft: 20,
          }}
        >
          <p
            data-block-id={block.id}
            style={{
              color: '#5c5147',
              fontFamily: font.serif,
              fontSize: 15,
              fontStyle: 'italic',
              lineHeight: 1.55,
              margin: 0,
            }}
          >
            <BlockText
              activeHighlightId={activeHighlightId}
              block={block}
              highlights={highlights}
              onHighlightClick={onHighlightClick}
            />
          </p>
        </blockquote>
      );
    }

    if (block.type === 'listitem') {
      return (
        <div
          key={block.id}
          style={{
            alignItems: 'flex-start',
            display: 'flex',
            gap: 10,
            margin: '0 0 10px',
          }}
        >
          <span
            style={{
              color: colors.muted,
              flexShrink: 0,
              fontFamily: font.sans,
              fontSize: 16,
              lineHeight: 1.75,
              userSelect: 'none',
            }}
          >
            •
          </span>
          <p
            data-block-id={block.id}
            style={{
              color: colors.body,
              fontFamily: font.sans,
              fontSize: 16,
              lineHeight: 1.75,
              margin: 0,
            }}
          >
            <BlockText
              activeHighlightId={activeHighlightId}
              block={block}
              highlights={highlights}
              onHighlightClick={onHighlightClick}
            />
          </p>
        </div>
      );
    }

    return (
      <p
        key={block.id}
        data-block-id={block.id}
        style={{
          color: colors.body,
          fontFamily: font.sans,
          fontSize: 16,
          lineHeight: 1.75,
          margin: '0 0 18px',
        }}
      >
        <BlockText
          activeHighlightId={activeHighlightId}
          block={block}
          highlights={highlights}
          onHighlightClick={onHighlightClick}
        />
      </p>
    );
  };
  return (
    <>
      {blocks.map((block) => {
        const view = renderBlock(block);
        if (
          !onKeep ||
          !onUse ||
          block.type === 'heading' ||
          !parseToVisibleSegments(block.text)
            .map((segment) => segment.text)
            .join('')
            .trim()
        ) {
          return view;
        }
        return (
          <div key={block.id}>
            {view}
            <ReflectionBlockActions
              enabled
              onKeep={() => onKeep(block)}
              onUse={() => onUse(block)}
            />
          </div>
        );
      })}
    </>
  );
};

const formatNoteDate = (value: string | undefined): string | null => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

/** Figma: "Private note • Aug 20, 2026", dropping the date when it is unknown. */
const formatPrivateNoteCaption = (value: string | undefined): string => {
  const date = formatNoteDate(value);

  return date ? `Private note • ${date}` : 'Private note';
};

const noteAuthorName = (author?: MemberReflectionNoteAuthor): string =>
  (author?.firstName ?? author?.lastName ?? '').trim();

const SavedArtifactNoteCard = ({
  alwaysShowActions = false,
  author,
  deleteDisabled,
  disabled,
  editDraft,
  isEditing: controlledIsEditing,
  note,
  onCancelEdit,
  onDelete,
  onEditDraftChange,
  onEditSaved,
  onHeightChange,
  onStartEdit,
  onUpdate,
  style,
}: {
  alwaysShowActions?: boolean;
  author?: MemberReflectionNoteAuthor;
  deleteDisabled?: boolean;
  disabled?: boolean;
  editDraft?: string;
  isEditing?: boolean;
  note: MemberReflectionArtifactHighlight;
  onCancelEdit?: () => void;
  onDelete?: (id: string) => Promise<void> | void;
  onEditDraftChange?: (draft: string) => void;
  onEditSaved?: () => void;
  onHeightChange?: (id: string, height: number) => void;
  onStartEdit?: (id: string, currentNote?: string) => void;
  onUpdate?: (id: string, note: string) => Promise<void> | void;
  style?: CSSProperties;
}) => {
  const articleRef = useRef<HTMLElement | null>(null);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [localIsEditing, setLocalIsEditing] = useState(false);
  const [localDraft, setLocalDraft] = useState(note.note ?? '');
  const [displayNote, setDisplayNote] = useState(note.note ?? '');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isSavingRef = useRef(false);
  const isDeletingRef = useRef(false);
  const locallySavedNoteRef = useRef<{
    id: string;
    note: string;
    saveCount: number;
    updatedAt?: string;
  } | null>(null);
  const isControlledEdit = controlledIsEditing !== undefined;
  const isEditing = controlledIsEditing ?? localIsEditing;
  const draft = editDraft ?? localDraft;
  const setDraft = onEditDraftChange ?? setLocalDraft;
  const trimmedDraft = draft.trim();
  const isUpdateDisabled = disabled || isSaving;
  const isDeleteDisabled = deleteDisabled || isDeleting;
  // MEN-4684: a "..." menu (Edit/Delete) is revealed on hover, matching the
  // Google-Docs-comment pattern from Itai's Figma mock (node 18519:4409). It
  // stays mounted so keyboard users can Tab to it -- focus inside the card
  // reveals it the same way hovering does.
  const areActionsRevealed =
    alwaysShowActions ||
    isHovered ||
    isFocusWithin ||
    isConfirmingDelete ||
    isMenuOpen;
  const authorName = noteAuthorName(author);

  // MEN-4684 review: the stacking math in calculateNoteCardTops only had a
  // fixed height estimate to go on, so a card taller than that estimate
  // (a long note, or one showing an edit textarea/error) could overlap the
  // next card. Report the card's real rendered height -- including those
  // taller states -- so the parent can stack using actual measurements.
  useEffect(() => {
    if (!onHeightChange) {
      return undefined;
    }
    const element = articleRef.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      onHeightChange(note.id, (entry.target as HTMLElement).offsetHeight);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [note.id, onHeightChange]);

  useEffect(() => {
    const nextNote = note.note ?? '';
    const locallySavedNote = locallySavedNoteRef.current;
    const remoteUpdatedAt = note.updatedAt ?? note.createdAt;

    if (locallySavedNote?.id === note.id) {
      const remoteCaughtUp = locallySavedNote.note === nextNote;
      const remoteChangedSinceSave = Boolean(
        locallySavedNote.updatedAt &&
        remoteUpdatedAt &&
        locallySavedNote.updatedAt !== remoteUpdatedAt,
      );
      const hasNewerLocalSavePending = locallySavedNote.saveCount > 1;

      if (
        !remoteCaughtUp &&
        (hasNewerLocalSavePending || !remoteChangedSinceSave)
      ) {
        return;
      }
      locallySavedNoteRef.current = null;
    } else if (locallySavedNote) {
      locallySavedNoteRef.current = null;
    }

    setDisplayNote(nextNote);
    if (!isEditing) {
      setLocalDraft(nextNote);
    }
  }, [isEditing, note.createdAt, note.id, note.note, note.updatedAt]);

  const handleCancel = () => {
    setError(null);
    if (isControlledEdit) {
      onCancelEdit?.();
      return;
    }
    setDraft(displayNote);
    setLocalIsEditing(false);
  };

  const handleSave = async () => {
    if (!trimmedDraft || !onUpdate || isUpdateDisabled || isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    setError(null);
    try {
      await onUpdate(note.id, trimmedDraft);
      locallySavedNoteRef.current = {
        id: note.id,
        note: trimmedDraft,
        saveCount:
          locallySavedNoteRef.current?.id === note.id
            ? locallySavedNoteRef.current.saveCount + 1
            : 1,
        updatedAt: note.updatedAt ?? note.createdAt,
      };
      setDisplayNote(trimmedDraft);
      setDraft(trimmedDraft);
      if (isControlledEdit) {
        onEditSaved?.();
      } else {
        setLocalIsEditing(false);
      }
    } catch {
      setError('Failed to save note. Please try again.');
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete || isDeleteDisabled || isDeletingRef.current) {
      return;
    }

    isDeletingRef.current = true;
    setIsDeleting(true);
    setError(null);
    try {
      await onDelete(note.id);
      setIsConfirmingDelete(false);
    } catch {
      setError('Failed to delete note. Please try again.');
    } finally {
      isDeletingRef.current = false;
      setIsDeleting(false);
    }
  };

  return (
    <article
      ref={articleRef}
      aria-label="Private note"
      className="fs-mask"
      data-dd-privacy="mask"
      data-note-card-id={note.id}
      style={{ ...pendingNoteStyles, position: 'relative', ...style }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setIsFocusWithin(false);
          setIsMenuOpen(false);
        }
      }}
      onFocus={() => setIsFocusWithin(true)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setIsMenuOpen(false);
      }}
    >
      {error ? (
        <Alert severity="error" style={{ marginBottom: 12 }}>
          {error}
        </Alert>
      ) : null}
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          gap: 8,
          marginBottom: 8,
        }}
      >
        <Avatar
          firstName={author?.firstName}
          lastName={author?.lastName}
          size="s"
          src={author?.avatarUrl ?? ''}
        />
        {authorName ? (
          <Typography
            color={colors.body}
            style={{ fontWeight: 600 }}
            variant="body2"
          >
            {authorName}
          </Typography>
        ) : null}
      </div>
      {isEditing ? (
        <>
          <textarea
            aria-label="Edit private note"
            disabled={isUpdateDisabled}
            style={textareaStyles}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <div style={noteActionsStyles}>
            <button
              disabled={isUpdateDisabled}
              style={{
                ...secondaryNoteButtonStyles,
                opacity: isUpdateDisabled ? 0.6 : 1,
              }}
              type="button"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              disabled={isUpdateDisabled || !trimmedDraft}
              style={{
                ...primaryNoteButtonStyles,
                opacity: isUpdateDisabled || !trimmedDraft ? 0.6 : 1,
              }}
              type="button"
              onClick={() => {
                handleSave().catch(() => undefined);
              }}
            >
              {isUpdateDisabled ? 'Saving…' : 'Save'}
            </button>
          </div>
        </>
      ) : (
        <>
          <Typography
            color={colors.body}
            style={{ whiteSpace: 'pre-wrap' }}
            variant="body2"
          >
            {displayNote}
          </Typography>
          <div style={{ marginTop: 10 }}>
            <Typography color={colors.caption} variant="caption">
              {formatPrivateNoteCaption(note.updatedAt ?? note.createdAt)}
            </Typography>
          </div>
          <div
            style={{
              opacity: areActionsRevealed ? 1 : 0,
              pointerEvents: areActionsRevealed ? 'auto' : 'none',
              position: 'absolute',
              right: 12,
              top: 12,
              transition: 'opacity 120ms ease',
            }}
          >
            <IconButton
              aria-label="Note actions"
              size="small"
              onClick={() => setIsMenuOpen((currentIsOpen) => !currentIsOpen)}
            >
              <Icon fontSize="small">more_vert</Icon>
            </IconButton>
            {isMenuOpen ? (
              <div
                aria-label="Note actions menu"
                role="menu"
                style={{
                  background: '#FFFFFF',
                  border: `1px solid ${colors.divider}`,
                  borderRadius: 10,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
                  minWidth: 132,
                  padding: '6px 0',
                  position: 'absolute',
                  right: 0,
                  top: 32,
                  zIndex: 2,
                }}
              >
                <button
                  disabled={disabled || !onUpdate}
                  role="menuitem"
                  style={{
                    background: 'transparent',
                    border: 0,
                    color: colors.body,
                    cursor: disabled || !onUpdate ? 'default' : 'pointer',
                    fontFamily: font.sans,
                    fontSize: 14,
                    opacity: disabled || !onUpdate ? 0.6 : 1,
                    padding: '10px 14px',
                    textAlign: 'left',
                    width: '100%',
                  }}
                  type="button"
                  onClick={() => {
                    setIsMenuOpen(false);
                    if (onStartEdit) {
                      onStartEdit(note.id, displayNote);
                    } else {
                      setLocalIsEditing(true);
                    }
                  }}
                >
                  Edit
                </button>
                <button
                  disabled={deleteDisabled || !onDelete}
                  role="menuitem"
                  style={{
                    background: 'transparent',
                    border: 0,
                    color: '#A33A2A',
                    cursor: deleteDisabled || !onDelete ? 'default' : 'pointer',
                    fontFamily: font.sans,
                    fontSize: 14,
                    opacity: deleteDisabled || !onDelete ? 0.6 : 1,
                    padding: '10px 14px',
                    textAlign: 'left',
                    width: '100%',
                  }}
                  type="button"
                  onClick={() => {
                    setIsMenuOpen(false);
                    setIsConfirmingDelete(true);
                  }}
                >
                  Delete
                </button>
              </div>
            ) : null}
          </div>
          {isConfirmingDelete ? (
            <div
              aria-label="Delete private note"
              role="alertdialog"
              style={{
                background: colors.pageBg,
                border: `1px solid ${colors.divider}`,
                borderRadius: 8,
                marginTop: 12,
                padding: 12,
              }}
            >
              <p
                style={{
                  color: colors.body,
                  fontFamily: font.sans,
                  fontSize: 13,
                  lineHeight: 1.4,
                  margin: '0 0 10px',
                }}
              >
                Delete this private note? This cannot be undone.
              </p>
              <div style={noteActionsStyles}>
                <button
                  disabled={isDeleteDisabled}
                  style={{
                    ...secondaryNoteButtonStyles,
                    opacity: isDeleteDisabled ? 0.6 : 1,
                  }}
                  type="button"
                  onClick={() => setIsConfirmingDelete(false)}
                >
                  Cancel
                </button>
                <button
                  disabled={isDeleteDisabled}
                  style={{
                    ...primaryNoteButtonStyles,
                    background: '#A33A2A',
                    border: '1px solid #A33A2A',
                    opacity: isDeleteDisabled ? 0.6 : 1,
                  }}
                  type="button"
                  onClick={() => {
                    handleDelete().catch(() => undefined);
                  }}
                >
                  {isDeleteDisabled ? 'Deleting…' : 'Delete note'}
                </button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </article>
  );
};

export const SavedArtifactNotesList = ({
  alwaysShowActions = false,
  author,
  cardTops,
  deleteDisabled,
  disabled,
  editDraft,
  editingNoteId,
  isBusy,
  measuredCardHeights,
  notes,
  onCancelEdit,
  onDelete,
  onEditDraftChange,
  onEditSaved,
  onHeightChange,
  onStartEdit,
  onUpdate,
  positioned = false,
}: {
  alwaysShowActions?: boolean;
  author?: MemberReflectionNoteAuthor;
  cardTops?: Map<string, number>;
  deleteDisabled?: boolean;
  disabled?: boolean;
  editDraft?: string;
  editingNoteId?: string | null;
  isBusy?: boolean;
  measuredCardHeights?: Map<string, number>;
  notes: MemberReflectionArtifactHighlight[];
  onCancelEdit?: () => void;
  onDelete?: (id: string) => Promise<void> | void;
  onEditDraftChange?: (draft: string) => void;
  onEditSaved?: () => void;
  onHeightChange?: (id: string, height: number) => void;
  onStartEdit?: (id: string, currentNote?: string) => void;
  onUpdate?: (id: string, note: string) => Promise<void> | void;
  positioned?: boolean;
}) => {
  if (notes.length === 0) {
    return null;
  }

  // Uses each card's real measured height where available (falling back to
  // the estimate) so a tall bottommost card isn't clipped by a section
  // that's sized off the static estimate alone.
  const maxBottom = Math.max(
    0,
    ...notes.map(
      (note) =>
        (cardTops?.get(note.id) ?? 0) +
        (measuredCardHeights?.get(note.id) ?? estimatedSavedNoteCardHeight),
    ),
  );

  return (
    <section
      aria-busy={isBusy || undefined}
      aria-label="Saved private notes"
      style={
        positioned
          ? {
              minHeight: maxBottom,
              position: 'relative',
            }
          : { marginTop: 24 }
      }
    >
      {notes.map((note) => (
        <SavedArtifactNoteCard
          key={note.id}
          alwaysShowActions={alwaysShowActions}
          author={author}
          deleteDisabled={deleteDisabled}
          disabled={disabled}
          editDraft={editingNoteId === note.id ? editDraft : undefined}
          isEditing={editingNoteId === note.id ? true : undefined}
          note={note}
          style={
            positioned
              ? {
                  margin: 0,
                  position: 'absolute',
                  top: cardTops?.get(note.id) ?? 0,
                  width: '100%',
                }
              : undefined
          }
          onCancelEdit={editingNoteId === note.id ? onCancelEdit : undefined}
          onDelete={onDelete}
          onEditDraftChange={
            editingNoteId === note.id ? onEditDraftChange : undefined
          }
          onEditSaved={editingNoteId === note.id ? onEditSaved : undefined}
          onHeightChange={positioned ? onHeightChange : undefined}
          onStartEdit={onStartEdit}
          onUpdate={onUpdate}
        />
      ))}
    </section>
  );
};

export const PendingArtifactNoteComposer = ({
  autoFocus,
  disabled,
  onTextAreaReady,
  note,
  onCancel,
  onNoteChange,
  onSave,
  style,
  variant = 'card',
}: {
  autoFocus?: boolean;
  disabled?: boolean;
  onTextAreaReady?: (element: HTMLTextAreaElement | null) => void;
  note: string;
  onCancel: () => void;
  onNoteChange: (note: string) => void;
  onSave: (note: string) => Promise<void> | void;
  style?: CSSProperties;
  variant?: 'card' | 'mobileDock';
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const isSavingRef = useRef(false);
  const trimmedNote = note.trim();
  const saveDisabled = disabled || isSaving;
  const isMobileDock = variant === 'mobileDock';

  useEffect(() => {
    if (!autoFocus || disabled) {
      return;
    }
    textareaRef.current?.focus();
  }, [autoFocus, disabled]);

  const handleSave = async () => {
    if (!trimmedNote || saveDisabled || isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      await onSave(trimmedNote);
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  return (
    <aside
      aria-label="New private note"
      className="fs-mask"
      data-dd-privacy="mask"
      style={{
        ...pendingNoteStyles,
        ...(isMobileDock ? mobileComposerShellStyles : undefined),
        ...style,
      }}
    >
      <p
        style={{
          color: colors.body,
          display: 'block',
          fontFamily: font.sans,
          fontSize: 13,
          fontWeight: 600,
          margin: '0 0 8px',
        }}
      >
        Private note
      </p>
      {isMobileDock ? (
        <div style={mobileComposerInputRowStyles}>
          <textarea
            ref={(element) => {
              textareaRef.current = element;
              onTextAreaReady?.(element);
            }}
            aria-label="Private note"
            disabled={saveDisabled}
            placeholder="Add a private note…"
            style={mobileComposerTextareaStyles}
            value={note}
            onChange={(event) => onNoteChange(event.target.value)}
          />
          <IconButton
            aria-label="Save note"
            disabled={saveDisabled || !trimmedNote}
            size="small"
            style={{
              background: colors.sage,
              color: '#FFFFFF',
              flex: '0 0 auto',
              opacity: saveDisabled || !trimmedNote ? 0.6 : 1,
            }}
            onClick={() => {
              handleSave().catch(() => undefined);
            }}
          >
            <Icon fontSize="small">check</Icon>
          </IconButton>
        </div>
      ) : (
        <textarea
          ref={(element) => {
            textareaRef.current = element;
            onTextAreaReady?.(element);
          }}
          aria-label="Private note"
          disabled={saveDisabled}
          placeholder="Add a private note…"
          style={textareaStyles}
          value={note}
          onChange={(event) => onNoteChange(event.target.value)}
        />
      )}
      <div style={noteActionsStyles}>
        <button
          style={secondaryNoteButtonStyles}
          type="button"
          onClick={onCancel}
        >
          Cancel
        </button>
        {!isMobileDock ? (
          <button
            disabled={saveDisabled || !trimmedNote}
            style={{
              ...primaryNoteButtonStyles,
              opacity: saveDisabled || !trimmedNote ? 0.6 : 1,
            }}
            type="button"
            onClick={() => {
              handleSave().catch(() => undefined);
            }}
          >
            {saveDisabled ? 'Saving…' : 'Save note'}
          </button>
        ) : null}
      </div>
    </aside>
  );
};

const MobileArtifactNoteViewer = ({
  activeNoteId,
  author,
  deleteDisabled,
  disabled,
  draft,
  isEditing,
  isMenuOpen,
  notes,
  onCancelEdit,
  onActiveNoteMissing,
  onClose,
  onDelete,
  onDraftChange,
  onMenuOpenChange,
  onNavigate,
  onSaveEdit,
  onStartEdit,
}: {
  activeNoteId: string;
  author?: MemberReflectionNoteAuthor;
  deleteDisabled?: boolean;
  disabled?: boolean;
  draft: string;
  isEditing: boolean;
  isMenuOpen: boolean;
  notes: MemberReflectionArtifactHighlight[];
  onActiveNoteMissing: () => void;
  onCancelEdit: () => void;
  onClose: () => void;
  onDelete?: (id: string) => Promise<void> | void;
  onDraftChange: (draft: string) => void;
  onMenuOpenChange: (isOpen: boolean) => void;
  onNavigate: (noteId: string) => void;
  onSaveEdit: (note: string) => Promise<void> | void;
  onStartEdit: (noteId: string) => void;
}) => {
  const touchStartXRef = useRef<number | null>(null);
  const isDeletingRef = useRef(false);
  const isSavingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const activeIndex = notes.findIndex((note) => note.id === activeNoteId);
  const activeNote = activeIndex >= 0 ? notes[activeIndex] : null;
  const previousNote =
    activeIndex > 0 ? (notes[activeIndex - 1] ?? null) : null;
  const nextNote = activeIndex >= 0 ? (notes[activeIndex + 1] ?? null) : null;
  const trimmedDraft = draft.trim();
  const isUpdateDisabled = disabled || isSaving;
  const isDeleteDisabled = deleteDisabled || isDeleting;

  useEffect(() => {
    if (activeIndex < 0) {
      onActiveNoteMissing();
    }
  }, [activeIndex, onActiveNoteMissing]);

  if (!activeNote) {
    return null;
  }

  const navigatePrevious = () => {
    if (previousNote) {
      onNavigate(previousNote.id);
    }
  };
  const navigateNext = () => {
    if (nextNote) {
      onNavigate(nextNote.id);
    }
  };
  const handleSave = async () => {
    if (!trimmedDraft || isUpdateDisabled || isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;
    setError(null);
    setIsSaving(true);
    try {
      await onSaveEdit(trimmedDraft);
    } catch {
      setError('Failed to save note. Please try again.');
    } finally {
      // eslint-disable-next-line require-atomic-updates -- Synchronous tap guard reset after awaited save completes.
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };
  const handleDelete = async () => {
    if (!onDelete || isDeleteDisabled || isDeletingRef.current) {
      return;
    }
    // eslint-disable-next-line no-alert -- MEN-4358 requires native mobile confirm for destructive note deletion.
    if (!window.confirm('Delete this note? This cannot be undone.')) {
      onMenuOpenChange(false);
      return;
    }

    const fallbackNoteId = nextNote?.id ?? previousNote?.id ?? null;
    isDeletingRef.current = true;
    setError(null);
    setIsDeleting(true);
    try {
      await onDelete(activeNote.id);
      onMenuOpenChange(false);
      if (fallbackNoteId) {
        onNavigate(fallbackNoteId);
      } else {
        onClose();
      }
    } catch {
      setError('Failed to delete note. Please try again.');
    } finally {
      // eslint-disable-next-line require-atomic-updates -- Synchronous tap guard reset after awaited delete completes.
      isDeletingRef.current = false;
      setIsDeleting(false);
    }
  };

  return (
    <aside
      aria-label="Private note viewer"
      style={mobileNoteViewerStyles}
      onTouchEnd={(event) => {
        const startX = touchStartXRef.current;
        touchStartXRef.current = null;
        const endX = event.changedTouches[0]?.clientX;
        if (startX === null || endX === undefined) {
          return;
        }
        const deltaX = endX - startX;
        if (Math.abs(deltaX) < 40) {
          return;
        }
        if (deltaX > 0) {
          navigatePrevious();
        } else {
          navigateNext();
        }
      }}
      onTouchStart={(event) => {
        touchStartXRef.current = event.touches[0]?.clientX ?? null;
      }}
    >
      <div style={mobileNoteViewerToolbarStyles}>
        <div style={{ display: 'flex', gap: 16 }}>
          <IconButton
            aria-label="Previous note"
            disabled={!previousNote}
            size="small"
            onClick={navigatePrevious}
          >
            <Icon fontSize="small">chevron_left</Icon>
          </IconButton>
          <IconButton
            aria-label="Next note"
            disabled={!nextNote}
            size="small"
            onClick={navigateNext}
          >
            <Icon fontSize="small">chevron_right</Icon>
          </IconButton>
        </div>
        <IconButton aria-label="Close notes" size="small" onClick={onClose}>
          <Icon fontSize="small">close</Icon>
        </IconButton>
      </div>
      <article aria-label="Private note" style={{ position: 'relative' }}>
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            gap: 10,
            marginBottom: 6,
            paddingRight: 34,
          }}
        >
          <Avatar
            firstName={author?.firstName}
            lastName={author?.lastName}
            size="s"
            src={author?.avatarUrl ?? ''}
          />
          <span
            style={{
              color: colors.body,
              fontFamily: font.sans,
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Private note
          </span>
          {!isEditing ? (
            <IconButton
              aria-label="Note actions"
              size="small"
              style={{ position: 'absolute', right: 0, top: -6 }}
              onClick={() => onMenuOpenChange(!isMenuOpen)}
            >
              <Icon fontSize="small">more_horiz</Icon>
            </IconButton>
          ) : null}
        </div>
        {isMenuOpen ? (
          <div
            aria-label="Note actions menu"
            role="menu"
            style={{
              background: '#FFFFFF',
              border: `1px solid ${colors.divider}`,
              borderRadius: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
              minWidth: 132,
              padding: '6px 0',
              position: 'absolute',
              right: 0,
              top: 28,
              zIndex: 2,
            }}
          >
            <button
              disabled={disabled}
              role="menuitem"
              style={{
                background: 'transparent',
                border: 0,
                color: colors.body,
                cursor: disabled ? 'default' : 'pointer',
                fontFamily: font.sans,
                fontSize: 14,
                opacity: disabled ? 0.6 : 1,
                padding: '10px 14px',
                textAlign: 'left',
                width: '100%',
              }}
              type="button"
              onClick={() => {
                onStartEdit(activeNote.id);
              }}
            >
              Edit
            </button>
            <button
              disabled={isDeleteDisabled || !onDelete}
              role="menuitem"
              style={{
                background: 'transparent',
                border: 0,
                color: '#A33A2A',
                cursor: isDeleteDisabled || !onDelete ? 'default' : 'pointer',
                fontFamily: font.sans,
                fontSize: 14,
                opacity: isDeleteDisabled || !onDelete ? 0.6 : 1,
                padding: '10px 14px',
                textAlign: 'left',
                width: '100%',
              }}
              type="button"
              onClick={() => {
                handleDelete().catch(() => undefined);
              }}
            >
              Remove
            </button>
          </div>
        ) : null}
        {error ? (
          <Alert severity="error" style={{ margin: '0 0 12px 34px' }}>
            {error}
          </Alert>
        ) : null}
        {isEditing ? (
          <>
            <textarea
              aria-label="Edit private note"
              disabled={isUpdateDisabled}
              style={{
                ...textareaStyles,
                fontSize: 16,
                marginLeft: 34,
                width: 'calc(100% - 34px)',
              }}
              value={draft}
              onChange={(event) => onDraftChange(event.target.value)}
            />
            <div style={noteActionsStyles}>
              <button
                disabled={isUpdateDisabled}
                style={{
                  ...secondaryNoteButtonStyles,
                  opacity: isUpdateDisabled ? 0.6 : 1,
                }}
                type="button"
                onClick={onCancelEdit}
              >
                Cancel
              </button>
              <button
                disabled={isUpdateDisabled || !trimmedDraft}
                style={{
                  ...primaryNoteButtonStyles,
                  opacity: isUpdateDisabled || !trimmedDraft ? 0.6 : 1,
                }}
                type="button"
                onClick={() => {
                  handleSave().catch(() => undefined);
                }}
              >
                {isUpdateDisabled ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        ) : (
          <>
            <p
              style={{
                color: colors.body,
                fontFamily: font.sans,
                fontSize: 16,
                lineHeight: 1.5,
                margin: '0 0 12px 34px',
                whiteSpace: 'pre-wrap',
              }}
            >
              {activeNote.note}
            </p>
            <Typography
              color={colors.caption}
              component="p"
              style={{ margin: '0 0 0 34px' }}
              variant="caption"
            >
              {formatNoteDate(activeNote.updatedAt ?? activeNote.createdAt)}
            </Typography>
          </>
        )}
      </article>
    </aside>
  );
};

// Owns its own scroll tracking so each scroll event re-renders only this
// button, not the whole artifact view; re-rendering the view per scroll event
// made the button visibly lag behind the text.
const ArtifactSelectionButton = ({
  initialPosition,
  isMobile,
  label,
  onConfirm,
  range,
}: {
  initialPosition: ArtifactSelectionButtonPosition;
  isMobile: boolean;
  label: string;
  onConfirm: () => void;
  range: Range | null;
}) => {
  const [position, setPosition] = useState(initialPosition);

  useEffect(() => {
    setPosition(initialPosition);
  }, [initialPosition]);

  useEffect(() => {
    if (isMobile || !range) {
      return undefined;
    }

    const onScroll = () => {
      const rect = range.getBoundingClientRect();
      setPosition({ bottom: rect.bottom, right: rect.right, top: rect.top });
    };

    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [isMobile, range]);

  const positionStyles: CSSProperties = isMobile
    ? mobileSelectionButtonStyles
    : {
        left: `clamp(8px, ${position.right + 16}px, calc(100vw - 104px))`,
        top: `${getSelectionButtonTop(position)}px`,
      };

  return (
    <button
      style={{ ...selectionButtonStyles, ...positionStyles }}
      type="button"
      onClick={onConfirm}
      onMouseDown={(event) => {
        event.preventDefault();
      }}
    >
      {label}
    </button>
  );
};

const FeedbackButton = ({
  disabled,
  isSelected,
  label,
  offIcon,
  onClick,
  selectedIcon,
}: {
  disabled?: boolean;
  isSelected: boolean;
  label: string;
  offIcon: string;
  onClick?: () => void;
  selectedIcon: string;
}) => (
  <Tooltip title={label}>
    <span>
      <IconButton
        aria-label={label}
        aria-pressed={isSelected}
        disabled={disabled}
        size="small"
        onClick={onClick}
      >
        <Icon fontSize="small">{isSelected ? selectedIcon : offIcon}</Icon>
      </IconButton>
    </span>
  </Tooltip>
);

const renderCopyTooltipTitle = (
  copyState: MemberReflectionCopyState,
  fallbackLabel: string,
) => {
  if (copyState === 'copied') {
    return 'Copied!';
  }
  if (copyState === 'error') {
    return 'Copy failed';
  }
  return fallbackLabel;
};

const renderStatus = ({
  errorAction,
  errorMessage,
  status,
  statusCopy,
}: Pick<
  MemberReflectionArtifactViewProps,
  'errorAction' | 'errorMessage' | 'status' | 'statusCopy'
>) => {
  if (status === 'loading') {
    return (
      <div className="flex items-center gap-3" style={{ color: colors.muted }}>
        <CircularProgress size={18} />
        <Typography color="text.secondary" variant="body2">
          {statusCopy?.loading || 'Loading reflection…'}
        </Typography>
      </div>
    );
  }

  if (status === 'not-found') {
    return (
      <Alert severity="warning">
        {errorMessage || statusCopy?.notFound || 'Reflection not found.'}
      </Alert>
    );
  }

  if (status === 'error') {
    return (
      <Alert
        action={
          errorAction ? (
            <Button
              color="inherit"
              href={errorAction.href}
              rel="noopener noreferrer"
              size="small"
              target="_blank"
            >
              {errorAction.label}
            </Button>
          ) : undefined
        }
        severity="error"
      >
        {errorMessage || statusCopy?.error || 'Failed to load reflection.'}
      </Alert>
    );
  }

  return null;
};

const HeaderActions = ({
  copyAriaLabel,
  copyState,
  copyTooltipLabel,
  feedbackValue,
  isFeedbackSubmitting,
  onCopy,
  onFeedbackButtonClick,
}: Pick<
  MemberReflectionArtifactViewProps,
  | 'copyAriaLabel'
  | 'copyState'
  | 'copyTooltipLabel'
  | 'feedbackValue'
  | 'isFeedbackSubmitting'
  | 'onCopy'
> & {
  onFeedbackButtonClick?: (
    value: Exclude<MemberReflectionFeedbackValue, null>,
  ) => void;
}) => (
  <div style={actionBarStyles}>
    <Tooltip
      title={renderCopyTooltipTitle(
        copyState ?? 'idle',
        copyTooltipLabel || 'Copy reflection text',
      )}
    >
      <span>
        <IconButton
          aria-label={copyAriaLabel || 'Copy reflection text'}
          disabled={!onCopy}
          size="small"
          onClick={onCopy}
        >
          <Icon fontSize="small">content_copy</Icon>
        </IconButton>
      </span>
    </Tooltip>
    <FeedbackButton
      disabled={isFeedbackSubmitting || !onFeedbackButtonClick}
      isSelected={feedbackValue === 'positive'}
      label="What works well"
      offIcon="thumb_up_off_alt"
      selectedIcon="thumb_up"
      onClick={() => onFeedbackButtonClick?.('positive')}
    />
    <FeedbackButton
      disabled={isFeedbackSubmitting || !onFeedbackButtonClick}
      isSelected={feedbackValue === 'negative'}
      label="What could be improved"
      offIcon="thumb_down_off_alt"
      selectedIcon="thumb_down"
      onClick={() => onFeedbackButtonClick?.('negative')}
    />
  </div>
);

const getSessionLabel = ({
  participantLabel,
  sessionDateLabel,
  viewerContext = 'member',
}: Pick<
  MemberReflectionArtifactViewProps,
  'participantLabel' | 'sessionDateLabel' | 'viewerContext'
>) => {
  if (sessionDateLabel) {
    const sessionPrefix = viewerContext === 'coach' ? 'For the' : 'For your';
    return `${sessionPrefix} ${sessionDateLabel} session${participantLabel ? ` with ${participantLabel}` : ''}`;
  }
  if (participantLabel) {
    return `With ${participantLabel}`;
  }
  return undefined;
};

const getStartBlockId = (blockId: string): string =>
  blockId.split(':')[0] ?? blockId;

const getArtifactTextSelectionKey = (
  selection: ArtifactTextSelection,
): string =>
  `${selection.blockId}:${selection.startIndex}:${selection.endIndex}:${selection.text}`;

const findContentBlockElement = (
  contentElement: HTMLElement | null,
  blockId: string,
): HTMLElement | null => {
  if (!contentElement) {
    return null;
  }

  return (
    Array.from(
      contentElement.querySelectorAll<HTMLElement>('[data-block-id]'),
    ).find((element) => element.dataset.blockId === blockId) ?? null
  );
};

const getHashTargetId = (): string => {
  const hash = window.location.hash.replace(/^#/u, '');
  if (!hash) {
    return '';
  }

  try {
    return decodeURIComponent(hash).trim();
  } catch {
    return hash.trim();
  }
};

const findHighlightElement = (
  contentElement: HTMLElement | null,
  id: string,
): Element | null => {
  if (!contentElement) {
    return null;
  }

  return (
    Array.from(contentElement.querySelectorAll('[data-highlight-id]')).find(
      (element) => element.getAttribute('data-highlight-id') === id,
    ) ?? null
  );
};

const findNoteHashTarget = (id: string): Element | null => {
  const noteCard = Array.from(
    document.querySelectorAll('[data-note-card-id]'),
  ).find((element) => element.getAttribute('data-note-card-id') === id);

  if (noteCard) {
    return noteCard;
  }

  return findHighlightElement(document.body, id);
};

const sortNotesByArtifactPosition = (
  notes: MemberReflectionArtifactHighlight[],
): MemberReflectionArtifactHighlight[] =>
  [...notes].sort((a, b) => {
    const aBlock = blockNum(getStartBlockId(a.blockId));
    const bBlock = blockNum(getStartBlockId(b.blockId));
    if (aBlock !== bBlock) {
      return aBlock - bBlock;
    }
    return a.startIndex - b.startIndex;
  });

const calculateNoteCardTops = ({
  contentElement,
  hasPendingError,
  measuredCardHeights,
  pendingSelection,
  savedNotes,
}: {
  contentElement: HTMLElement | null;
  hasPendingError?: boolean;
  measuredCardHeights?: Map<string, number>;
  pendingSelection: ArtifactTextSelection | null;
  savedNotes: MemberReflectionArtifactHighlight[];
}): Map<string, number> => {
  const tops = new Map<string, number>();
  const containerTop = contentElement
    ? contentElement.getBoundingClientRect().top + window.scrollY
    : 0;
  const placements: Array<{
    desiredTop: number;
    estimatedHeight: number;
    id: string;
  }> = [];

  for (const note of savedNotes) {
    const startBlockId = getStartBlockId(note.blockId);
    const blockElement = findContentBlockElement(contentElement, startBlockId);
    const blockTop = blockElement
      ? blockElement.getBoundingClientRect().top + window.scrollY - containerTop
      : placements.length * (estimatedSavedNoteCardHeight + noteCardStackGap);
    const blockTextLength = Math.max(blockElement?.textContent?.length ?? 1, 1);
    const blockHeight = blockElement?.offsetHeight ?? 0;
    const proportionalOffset =
      (note.startIndex / blockTextLength) * blockHeight;

    placements.push({
      desiredTop: Math.max(blockTop + proportionalOffset, 0),
      // Real rendered height once this card has been measured (covers
      // multiline notes, edit/error states -- anything taller than the
      // estimate); falls back to the estimate only until that first
      // measurement arrives (see the ResizeObserver wiring below).
      estimatedHeight:
        measuredCardHeights?.get(note.id) ?? estimatedSavedNoteCardHeight,
      id: note.id,
    });
  }

  if (pendingSelection) {
    const startBlockId = getStartBlockId(pendingSelection.blockId);
    const blockElement = findContentBlockElement(contentElement, startBlockId);
    const blockTop = blockElement
      ? blockElement.getBoundingClientRect().top + window.scrollY - containerTop
      : pendingSelection.absTop - containerTop;
    const blockTextLength = Math.max(blockElement?.textContent?.length ?? 1, 1);
    const blockHeight = blockElement?.offsetHeight ?? 0;
    const proportionalOffset =
      (pendingSelection.startIndex / blockTextLength) * blockHeight;

    placements.push({
      desiredTop: Math.max(blockTop + proportionalOffset, 0),
      estimatedHeight:
        estimatedPendingNoteCardHeight +
        (hasPendingError ? estimatedPendingNoteErrorHeight : 0),
      id: 'pending-artifact-note',
    });
  }

  let lastBottom = 0;
  for (const placement of placements.sort(
    (a, b) => a.desiredTop - b.desiredTop,
  )) {
    const top = Math.max(placement.desiredTop, lastBottom + noteCardStackGap);
    tops.set(placement.id, top);
    lastBottom = top + placement.estimatedHeight;
  }

  return tops;
};

const HeaderMetadata = ({
  artifactTitle,
  generationDateLabel,
  isMobile,
  participantLabel,
  sessionDateLabel,
  viewerContext,
}: Pick<
  MemberReflectionArtifactViewProps,
  | 'artifactTitle'
  | 'generationDateLabel'
  | 'participantLabel'
  | 'sessionDateLabel'
  | 'viewerContext'
> & { isMobile: boolean }) => {
  const sessionLabel = getSessionLabel({
    participantLabel,
    sessionDateLabel,
    viewerContext,
  });
  const generatedLabel = generationDateLabel
    ? `Generated ${generationDateLabel}`
    : undefined;

  if (isMobile) {
    return (
      <div style={mobileHeaderMetaStyles}>
        <p style={headerTitleStyles}>{artifactTitle}</p>
        {sessionLabel ? (
          <p style={headerSessionStyles}>{sessionLabel}</p>
        ) : null}
        {generatedLabel ? (
          <p style={headerGeneratedStyles}>{generatedLabel}</p>
        ) : null}
      </div>
    );
  }

  return (
    <div style={desktopHeaderLeftStyles}>
      <p style={headerTitleStyles}>{artifactTitle}</p>
      {sessionLabel ? (
        <>
          <span style={headerDividerStyles}>•</span>
          <p style={headerSessionStyles}>{sessionLabel}</p>
        </>
      ) : null}
      {generatedLabel ? (
        <>
          <span style={headerDividerStyles}>•</span>
          <p style={{ ...headerSessionStyles, fontWeight: 400 }}>
            {generatedLabel}
          </p>
        </>
      ) : null}
    </div>
  );
};

export const MemberReflectionArtifactView = ({
  artifactHighlights = EMPTY_ARTIFACT_HIGHLIGHTS,
  artifactNoteAuthor,
  artifactNotesEnabled = false,
  artifactTitle = 'Reflection',
  coachSelectionCard,
  contentUnavailableMessage = 'Reflection content is not available yet.',
  copyAriaLabel,
  copyState = 'idle',
  copyTooltipLabel,
  errorAction,
  errorMessage,
  feedbackValue = null,
  generationDateLabel,
  introDismissError,
  isArtifactNoteDeleting = false,
  isArtifactNoteUpdating = false,
  isDismissingIntro = false,
  isFeedbackSubmitting = false,
  introCopy,
  markdown = '',
  onArtifactNoteCreate,
  onArtifactNoteDelete,
  onArtifactNoteUpdate,
  onArtifactTextSelection,
  onBack,
  onCopy,
  onDismissIntro,
  onFeedback,
  participantLabel,
  reflectionKeep,
  onManageSavedNotes,
  sessionDateLabel,
  showIntroCallout = true,
  showSavedNotesLink = false,
  sourceReflectionLink,
  status,
  statusCopy,
  survey,
  transcriptHref,
  viewerContext = 'member',
}: MemberReflectionArtifactViewProps) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const isReady = status === 'ready';
  const keepEnabled =
    viewerContext === 'member' &&
    artifactNotesEnabled &&
    isReady &&
    Boolean(reflectionKeep?.enabled) &&
    Boolean(
      reflectionKeep &&
      typeof reflectionKeep.revision === 'number' &&
      reflectionKeep.revision > 0,
    );
  const [keepSuspended, setKeepSuspended] = useState(false);
  const [promptDraft, setPromptDraft] = useState<string | null>(null);
  const [promptSuspended, setPromptSuspended] = useState(false);
  const [mobileViewportBottomInset, setMobileViewportBottomInset] = useState(0);
  const contentRef = useRef<HTMLElement>(null);
  const mobilePendingNoteInputRef = useRef<HTMLTextAreaElement | null>(null);
  const mobilePendingNoteDockRef = useRef<HTMLDivElement | null>(null);
  const [pendingSelection, setPendingSelection] =
    useState<ArtifactTextSelection | null>(null);
  const pendingSelectionRef = useRef<ArtifactTextSelection | null>(null);
  const pendingSelectionVersionRef = useRef(0);
  const [noteInteractionState, setNoteInteractionState] =
    useState<ArtifactNoteInteractionState>({
      type: 'idle',
    });
  const activeHighlightClearTimerRef = useRef<number | null>(null);
  const activeHighlightFlashTimerRef = useRef<number | null>(null);
  const [activeScrolledHighlightId, setActiveScrolledHighlightId] = useState<
    string | null
  >(null);
  const [pendingNoteError, setPendingNoteError] = useState<string | null>(null);
  const [optimisticCreatedNotes, setOptimisticCreatedNotes] = useState<
    MemberReflectionArtifactHighlight[]
  >([]);
  const [locallyDeletedNoteIds, setLocallyDeletedNoteIds] = useState<
    Set<string>
  >(new Set());
  useEffect(() => {
    if (!isMobile || !window.visualViewport) {
      setMobileViewportBottomInset(0);
      return undefined;
    }

    const updateMobileViewportBottomInset = () => {
      const viewport = window.visualViewport;
      if (!viewport) {
        setMobileViewportBottomInset(0);
        return;
      }

      setMobileViewportBottomInset(
        Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop),
      );
    };

    updateMobileViewportBottomInset();
    window.visualViewport.addEventListener(
      'resize',
      updateMobileViewportBottomInset,
    );
    window.visualViewport.addEventListener(
      'scroll',
      updateMobileViewportBottomInset,
    );
    return () => {
      window.visualViewport?.removeEventListener(
        'resize',
        updateMobileViewportBottomInset,
      );
      window.visualViewport?.removeEventListener(
        'scroll',
        updateMobileViewportBottomInset,
      );
    };
  }, [isMobile]);
  useEffect(() => {
    if (!isMobile) {
      return undefined;
    }

    const displays = new Map<HTMLElement, string>();
    const widgetObservers = new Map<HTMLElement, MutationObserver>();
    const hideWidget = (widget: HTMLElement) => {
      const target = widget;
      if (!displays.has(target)) {
        displays.set(target, target.style.display);
      }
      if (!widgetObservers.has(target)) {
        const observer = new MutationObserver(() => hideWidget(target));
        observer.observe(target, {
          attributeFilter: ['style'],
          attributes: true,
        });
        widgetObservers.set(target, observer);
      }
      if (target.style.display !== 'none') {
        target.style.display = 'none';
      }
    };
    const hideWidgetsWithin = (node: Node) => {
      if (!(node instanceof HTMLElement)) {
        return;
      }
      if (node.matches('#cometchat__widget')) {
        hideWidget(node);
      }
      for (const widget of node.querySelectorAll<HTMLElement>(
        '#cometchat__widget',
      )) {
        hideWidget(widget);
      }
    };
    const bodyObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          hideWidgetsWithin(node);
        }
      }
    });

    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
    hideWidgetsWithin(document.body);

    return () => {
      bodyObserver.disconnect();
      for (const observer of widgetObservers.values()) {
        observer.disconnect();
      }
      for (const [widget, display] of displays) {
        if (widget.isConnected) {
          widget.style.display = display;
        }
      }
    };
  }, [isMobile]);
  const visibleArtifactHighlights = useMemo(
    () =>
      artifactHighlights.filter(
        (highlight) => !locallyDeletedNoteIds.has(highlight.id),
      ),
    [artifactHighlights, locallyDeletedNoteIds],
  );
  const pendingOptimisticCreatedNotes = useMemo(
    () =>
      optimisticCreatedNotes
        .filter(
          (optimisticNote) => !locallyDeletedNoteIds.has(optimisticNote.id),
        )
        .filter(
          (optimisticNote) =>
            !artifactHighlights.some(
              (highlight) => highlight.id === optimisticNote.id,
            ),
        )
        .map((note) => {
          const identity = note.sourceIdentity;
          if (
            !identity ||
            (identity.revision === reflectionKeep?.revision &&
              hasSupportedReflectionContracts(
                identity.readerContract,
                identity.anchorContract,
              ) &&
              verifyReflectionAnchor(projectReflection(markdown), {
                ...identity,
                selectedText: note.selectedText ?? '',
              }))
          )
            return note;
          return {
            ...note,
            blockId: SAVED_NOTE_UNLOCATED,
            startIndex: 0,
            endIndex: 0,
          };
        }),
    [
      artifactHighlights,
      locallyDeletedNoteIds,
      optimisticCreatedNotes,
      reflectionKeep?.revision,
      markdown,
    ],
  );
  const artifactNoteHighlights = useMemo(
    () =>
      pendingOptimisticCreatedNotes.length > 0
        ? [...visibleArtifactHighlights, ...pendingOptimisticCreatedNotes]
        : visibleArtifactHighlights,
    [pendingOptimisticCreatedNotes, visibleArtifactHighlights],
  );
  const savedNotes = useMemo(
    () =>
      artifactNotesEnabled
        ? sortNotesByArtifactPosition(
            artifactNoteHighlights.filter(
              (highlight) => highlight.note !== undefined,
            ),
          )
        : [],
    [artifactNoteHighlights, artifactNotesEnabled],
  );
  const [cardTops, setCardTops] = useState<Map<string, number>>(new Map());
  const [measuredCardHeights, setMeasuredCardHeights] = useState<
    Map<string, number>
  >(new Map());
  const handleCardHeightChange = useCallback((id: string, height: number) => {
    setMeasuredCardHeights((currentHeights) => {
      if (currentHeights.get(id) === height) {
        return currentHeights;
      }
      return new Map(currentHeights).set(id, height);
    });
  }, []);
  const hasNoteContent =
    artifactNotesEnabled && (savedNotes.length > 0 || pendingSelection);
  const showDesktopNotesColumn = Boolean(hasNoteContent && !isMobile);
  const desktopNotesColumnMinHeight = useMemo(() => {
    if (!showDesktopNotesColumn) {
      return desktopNotesColumnStyles.minHeight;
    }

    const savedBottoms = savedNotes.map(
      (note) =>
        (cardTops.get(note.id) ?? 0) +
        estimatedSavedNoteCardHeight +
        noteColumnBottomPadding,
    );
    const pendingBottom = pendingSelection
      ? (cardTops.get('pending-artifact-note') ?? 0) +
        estimatedPendingNoteCardHeight +
        (pendingNoteError ? estimatedPendingNoteErrorHeight : 0) +
        noteColumnBottomPadding
      : 0;

    return Math.max(1, pendingBottom, ...savedBottoms);
  }, [
    cardTops,
    pendingNoteError,
    pendingSelection,
    savedNotes,
    showDesktopNotesColumn,
  ]);
  const savedNoteMutationIsPending =
    isArtifactNoteDeleting || isArtifactNoteUpdating;
  const pendingNoteDraft =
    noteInteractionState.type === 'add' ? noteInteractionState.draft : '';
  const newKeepDraftSuspended =
    Boolean(pendingSelection?.sourceIdentity) &&
    (!keepEnabled || keepSuspended);
  const activeEditNote =
    noteInteractionState.type === 'edit'
      ? (savedNotes.find(
          (note) => note.id === noteInteractionState.activeNoteId,
        ) ?? null)
      : null;
  const activeEditDraft =
    noteInteractionState.type === 'edit' ? noteInteractionState.draft : '';
  const scrolledHashTargetIdRef = useRef<string | null>(null);
  // Retain the original authored draft/save target across source refreshes, but
  // never show its old coordinates or source-aligned card on a changed body.
  const pendingSourceLocated = useMemo(
    () =>
      !pendingSelection?.sourceIdentity ||
      (pendingSelection.sourceIdentity.revision === reflectionKeep?.revision &&
        verifyReflectionAnchor(projectReflection(markdown), {
          ...pendingSelection,
          scope: pendingSelection.sourceIdentity.scope,
          selectedText: pendingSelection.text,
        })),
    [pendingSelection, reflectionKeep?.revision, markdown],
  );
  const displayHighlights = useMemo(() => {
    if (!artifactNotesEnabled) {
      return EMPTY_ARTIFACT_HIGHLIGHTS;
    }

    if (!pendingSelection || !pendingSourceLocated) {
      return artifactNoteHighlights;
    }

    return [
      ...artifactNoteHighlights,
      {
        blockId: pendingSelection.blockId,
        endIndex: pendingSelection.endIndex,
        id: 'pending-artifact-note',
        startIndex: pendingSelection.startIndex,
      },
    ];
  }, [
    artifactNoteHighlights,
    artifactNotesEnabled,
    pendingSelection,
    pendingSourceLocated,
  ]);
  useEffect(() => {
    if (
      pendingOptimisticCreatedNotes.length !== optimisticCreatedNotes.length
    ) {
      setOptimisticCreatedNotes(pendingOptimisticCreatedNotes);
    }
  }, [optimisticCreatedNotes.length, pendingOptimisticCreatedNotes]);
  useEffect(() => {
    pendingSelectionRef.current = pendingSelection;
  }, [pendingSelection]);
  useEffect(() => {
    if (!isReady || !artifactNotesEnabled) {
      return;
    }

    const targetId = getHashTargetId();
    if (!targetId || scrolledHashTargetIdRef.current === targetId) {
      return;
    }

    const target = findNoteHashTarget(targetId);
    if (!target) {
      return;
    }

    target.scrollIntoView({ block: 'center' });
    scrolledHashTargetIdRef.current = targetId;
  }, [artifactNotesEnabled, displayHighlights, isReady, savedNotes]);

  const resetPendingNote = useCallback(() => {
    pendingSelectionVersionRef.current += 1;
    setPendingNoteError(null);
    setPendingSelection(null);
  }, []);
  const noteHasDirtyDraft = useCallback(
    (state: ArtifactNoteInteractionState) => {
      if (state.type === 'add') {
        return Boolean(state.draft.trim());
      }
      if (state.type === 'edit') {
        const savedNote = savedNotes.find(
          (note) => note.id === state.activeNoteId,
        );
        return state.draft !== (savedNote?.note ?? '');
      }
      return false;
    },
    [savedNotes],
  );
  const confirmDiscardNoteDraft = useCallback(
    (state: ArtifactNoteInteractionState = noteInteractionState) => {
      if (!noteHasDirtyDraft(state)) {
        return true;
      }
      // eslint-disable-next-line no-alert -- MEN-4358 requires native confirm before discarding typed note text.
      return window.confirm('Discard this note?');
    },
    [noteHasDirtyDraft, noteInteractionState],
  );
  const setIdleNoteInteraction = useCallback(() => {
    setNoteInteractionState({ type: 'idle' });
  }, []);
  useEffect(() => {
    if (noteInteractionState.type !== 'edit') {
      return;
    }

    if (
      savedNotes.some((note) => note.id === noteInteractionState.activeNoteId)
    ) {
      return;
    }

    setIdleNoteInteraction();
  }, [noteInteractionState, savedNotes, setIdleNoteInteraction]);
  const handleArtifactTextSelection = useCallback(
    (selection: ArtifactTextSelection) => {
      if (!confirmDiscardNoteDraft()) {
        return false;
      }

      pendingSelectionVersionRef.current += 1;
      setPendingNoteError(null);
      setPendingSelection(selection);
      setKeepSuspended(false);
      setNoteInteractionState({ draft: '', selection, type: 'add' });
      onArtifactTextSelection?.(selection);
      return true;
    },
    [confirmDiscardNoteDraft, onArtifactTextSelection],
  );
  const handleWholeBlockKeep = useCallback(
    (block: ContentBlock) => {
      if (!keepEnabled || !reflectionKeep?.revision) {
        return;
      }
      const text = parseToVisibleSegments(block.text)
        .map((segment) => segment.text)
        .join('');
      const element = findContentBlockElement(contentRef.current, block.id);
      handleArtifactTextSelection({
        absTop: (element?.getBoundingClientRect().top ?? 0) + window.scrollY,
        blockId: block.id,
        startIndex: 0,
        endIndex: text.length,
        text,
        sourceIdentity: {
          revision: reflectionKeep.revision,
          readerContract: REFLECTION_READER_CONTRACT,
          anchorContract: REFLECTION_ANCHOR_CONTRACT,
          scope: 'whole-block',
        },
      });
    },
    [keepEnabled, reflectionKeep?.revision, handleArtifactTextSelection],
  );
  const handleWholeBlockUse = useCallback(
    (block: ContentBlock) => {
      if (!keepEnabled) {
        return;
      }
      setPromptDraft(
        buildReflectionPrompt(
          parseToVisibleSegments(block.text)
            .map((segment) => segment.text)
            .join(''),
        ),
      );
      setPromptSuspended(false);
    },
    [keepEnabled],
  );
  const projectKeepSelection = useCallback(
    (
      selection: Selection | null,
      root: HTMLElement,
    ): ArtifactTextSelection | null => {
      if (!keepEnabled || !reflectionKeep?.revision) {
        return null;
      }
      const projected = readReflectionKeepSelection(selection, root, markdown);
      return projected
        ? {
            ...projected,
            sourceIdentity: {
              revision: reflectionKeep.revision,
              readerContract: REFLECTION_READER_CONTRACT,
              anchorContract: REFLECTION_ANCHOR_CONTRACT,
              scope: 'selection' as const,
            },
          }
        : null;
    },
    [keepEnabled, markdown, reflectionKeep?.revision],
  );
  useEffect(() => {
    if (!keepEnabled) {
      if (pendingSelection?.sourceIdentity) {
        setKeepSuspended(true);
      }
      if (promptDraft !== null) {
        setPromptSuspended(true);
      }
    }
  }, [keepEnabled, pendingSelection?.sourceIdentity, promptDraft]);

  const handleCancelPendingNote = useCallback(() => {
    resetPendingNote();
    setIdleNoteInteraction();
  }, [resetPendingNote, setIdleNoteInteraction]);
  const handleSavePendingNote = useCallback(
    async (note: string) => {
      if (
        !pendingSelection ||
        !onArtifactNoteCreate ||
        (pendingSelection.sourceIdentity && (!keepEnabled || keepSuspended))
      ) {
        return;
      }

      const selectionForSave = pendingSelection;
      const selectionKey = getArtifactTextSelectionKey(selectionForSave);
      const selectionVersionForSave = pendingSelectionVersionRef.current;
      const isCurrentSelection = () =>
        pendingSelectionVersionRef.current === selectionVersionForSave &&
        pendingSelectionRef.current &&
        getArtifactTextSelectionKey(pendingSelectionRef.current) ===
          selectionKey;

      setPendingNoteError(null);
      try {
        const createdNote = await onArtifactNoteCreate(selectionForSave, note);
        if (isCurrentSelection()) {
          if (createdNote?.id) {
            setLocallyDeletedNoteIds((currentNoteIds) => {
              const nextNoteIds = new Set(currentNoteIds);
              nextNoteIds.delete(createdNote.id);
              return nextNoteIds;
            });
            setOptimisticCreatedNotes((currentNotes) => [
              ...currentNotes.filter(
                (currentNote) => currentNote.id !== createdNote.id,
              ),
              createdNote,
            ]);
          }
          pendingSelectionVersionRef.current += 1;
          setPendingSelection(null);
          setIdleNoteInteraction();
        }
      } catch {
        if (isCurrentSelection()) {
          setPendingNoteError(
            selectionForSave.sourceIdentity
              ? 'The save outcome may be unknown. Check saved notes before deliberately retrying.'
              : 'Failed to save note. Please try again.',
          );
        }
      }
    },
    [
      onArtifactNoteCreate,
      pendingSelection,
      setIdleNoteInteraction,
      keepEnabled,
      keepSuspended,
    ],
  );
  const scrollToHighlight = useCallback((highlightId: string) => {
    const highlightElement = findHighlightElement(
      contentRef.current,
      highlightId,
    );
    if (!highlightElement) {
      return;
    }

    highlightElement.scrollIntoView({ block: 'center' });
    setActiveScrolledHighlightId(null);
    if (activeHighlightFlashTimerRef.current) {
      window.clearTimeout(activeHighlightFlashTimerRef.current);
    }
    if (activeHighlightClearTimerRef.current) {
      window.clearTimeout(activeHighlightClearTimerRef.current);
    }
    activeHighlightFlashTimerRef.current = window.setTimeout(() => {
      setActiveScrolledHighlightId(highlightId);
      activeHighlightFlashTimerRef.current = null;
      activeHighlightClearTimerRef.current = window.setTimeout(() => {
        setActiveScrolledHighlightId((currentHighlightId) =>
          currentHighlightId === highlightId ? null : currentHighlightId,
        );
        activeHighlightClearTimerRef.current = null;
      }, 1200);
    }, 180);
  }, []);
  useEffect(
    () => () => {
      if (activeHighlightFlashTimerRef.current) {
        window.clearTimeout(activeHighlightFlashTimerRef.current);
      }
      if (activeHighlightClearTimerRef.current) {
        window.clearTimeout(activeHighlightClearTimerRef.current);
      }
    },
    [],
  );
  const activeMobileNoteId =
    isMobile &&
    (noteInteractionState.type === 'view' ||
      noteInteractionState.type === 'edit')
      ? noteInteractionState.activeNoteId
      : null;
  useEffect(() => {
    if (!activeMobileNoteId) {
      return;
    }

    scrollToHighlight(activeMobileNoteId);
  }, [activeMobileNoteId, scrollToHighlight]);

  const discardPendingNote = useCallback(() => {
    if (!confirmDiscardNoteDraft()) {
      return false;
    }

    resetPendingNote();
    return true;
  }, [confirmDiscardNoteDraft, resetPendingNote]);
  const handleCloseMobileNoteViewer = useCallback(() => {
    if (!confirmDiscardNoteDraft()) {
      return;
    }
    setIdleNoteInteraction();
  }, [confirmDiscardNoteDraft, setIdleNoteInteraction]);
  const handleNavigateMobileNoteViewer = useCallback(
    (noteId: string) => {
      if (!confirmDiscardNoteDraft()) {
        return;
      }
      setNoteInteractionState({
        activeNoteId: noteId,
        isMenuOpen: false,
        type: 'view',
      });
    },
    [confirmDiscardNoteDraft],
  );
  const handleMobilePendingNoteCancel = useCallback(() => {
    if (discardPendingNote()) {
      setIdleNoteInteraction();
    }
  }, [discardPendingNote, setIdleNoteInteraction]);
  const handleMobilePendingNoteTapOutside = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (
        !isMobile ||
        noteInteractionState.type !== 'add' ||
        !mobilePendingNoteDockRef.current ||
        mobilePendingNoteDockRef.current.contains(event.target as Node)
      ) {
        return;
      }

      const targetElement =
        event.target instanceof Element ? event.target : null;
      if (targetElement?.closest('[data-highlight-id]')) {
        return;
      }

      event.preventDefault();
      if (discardPendingNote()) {
        setIdleNoteInteraction();
      }
    },
    [
      discardPendingNote,
      isMobile,
      noteInteractionState.type,
      setIdleNoteInteraction,
    ],
  );
  const handlePendingNoteDraftChange = useCallback((draft: string) => {
    setNoteInteractionState((currentState) =>
      currentState.type === 'add' ? { ...currentState, draft } : currentState,
    );
  }, []);
  const handleMobileHighlightClick = useCallback(
    (highlightId: string) => {
      if (!isMobile) {
        return;
      }
      const selectedText = window.getSelection()?.toString().trim();
      if (selectedText) {
        return;
      }
      if (!savedNotes.some((note) => note.id === highlightId)) {
        return;
      }
      if (!confirmDiscardNoteDraft()) {
        return;
      }
      if (noteInteractionState.type === 'add') {
        resetPendingNote();
      }
      setNoteInteractionState({
        activeNoteId: highlightId,
        isMenuOpen: false,
        type: 'view',
      });
    },
    [
      confirmDiscardNoteDraft,
      isMobile,
      noteInteractionState.type,
      resetPendingNote,
      savedNotes,
    ],
  );
  const handleStartEditArtifactNote = useCallback(
    (noteId: string, currentNote?: string) => {
      if (!confirmDiscardNoteDraft()) {
        return;
      }
      if (noteInteractionState.type === 'add') {
        resetPendingNote();
      }
      const note = savedNotes.find((savedNote) => savedNote.id === noteId);
      setNoteInteractionState({
        activeNoteId: noteId,
        draft: currentNote ?? note?.note ?? '',
        type: 'edit',
      });
    },
    [
      confirmDiscardNoteDraft,
      noteInteractionState.type,
      resetPendingNote,
      savedNotes,
    ],
  );
  const handleEditDraftChange = useCallback((draft: string) => {
    setNoteInteractionState((currentState) =>
      currentState.type === 'edit' ? { ...currentState, draft } : currentState,
    );
  }, []);
  const handleCancelEditArtifactNote = useCallback(() => {
    if (!confirmDiscardNoteDraft()) {
      return;
    }
    if (noteInteractionState.type !== 'edit') {
      return;
    }
    setNoteInteractionState({
      activeNoteId: noteInteractionState.activeNoteId,
      isMenuOpen: false,
      type: 'view',
    });
  }, [confirmDiscardNoteDraft, noteInteractionState]);
  const handleCancelDesktopEditArtifactNote = useCallback(() => {
    if (noteInteractionState.type !== 'edit') {
      return;
    }
    setIdleNoteInteraction();
  }, [noteInteractionState.type, setIdleNoteInteraction]);
  const handleSaveEditArtifactNote = useCallback(
    async (note: string) => {
      if (!onArtifactNoteUpdate || noteInteractionState.type !== 'edit') {
        return;
      }

      await onArtifactNoteUpdate(noteInteractionState.activeNoteId, note);
      setIdleNoteInteraction();
    },
    [noteInteractionState, onArtifactNoteUpdate, setIdleNoteInteraction],
  );
  const handleMobileMenuOpenChange = useCallback((isMenuOpen: boolean) => {
    setNoteInteractionState((currentState) => {
      if (currentState.type !== 'view') {
        return currentState;
      }
      return { ...currentState, isMenuOpen };
    });
  }, []);
  const handleDeleteArtifactNote = useCallback(
    async (noteId: string) => {
      if (!onArtifactNoteDelete) {
        return;
      }

      await onArtifactNoteDelete(noteId);
      setOptimisticCreatedNotes((currentNotes) =>
        currentNotes.filter((currentNote) => currentNote.id !== noteId),
      );
      setLocallyDeletedNoteIds((currentNoteIds) =>
        new Set(currentNoteIds).add(noteId),
      );
    },
    [onArtifactNoteDelete],
  );
  const updateCardTops = useCallback(() => {
    setCardTops(
      calculateNoteCardTops({
        contentElement: contentRef.current,
        hasPendingError: Boolean(pendingNoteError),
        measuredCardHeights,
        pendingSelection: pendingSourceLocated ? pendingSelection : null,
        savedNotes,
      }),
    );
  }, [
    measuredCardHeights,
    pendingNoteError,
    pendingSelection,
    pendingSourceLocated,
    savedNotes,
  ]);
  useLayoutEffect(() => {
    if (!showDesktopNotesColumn || !isReady) {
      return;
    }

    updateCardTops();
  }, [isReady, markdown, showDesktopNotesColumn, updateCardTops]);
  useEffect(() => {
    if (!showDesktopNotesColumn || !isReady) {
      return undefined;
    }

    window.addEventListener('resize', updateCardTops);
    return () => window.removeEventListener('resize', updateCardTops);
  }, [isReady, showDesktopNotesColumn, updateCardTops]);

  const {
    buttonPosition,
    confirmSelection,
    clearSelection,
    selection: currentSelection,
    selectionRange,
  } = useArtifactTextSelection({
    contentRef,
    enabled: artifactNotesEnabled && isReady,
    onSelection: handleArtifactTextSelection,
    projectSelection: keepEnabled ? projectKeepSelection : undefined,
  });
  useEffect(() => {
    if (!keepEnabled && currentSelection?.sourceIdentity) {
      clearSelection();
    }
  }, [keepEnabled, currentSelection, clearSelection]);
  const scrollToSelection = useCallback((selection: ArtifactTextSelection) => {
    const blockElement = findContentBlockElement(
      contentRef.current,
      getStartBlockId(selection.blockId),
    );
    blockElement?.scrollIntoView({ block: 'center' });
  }, []);
  const handleConfirmTextSelection = useCallback(() => {
    const confirmedSelection = flushSync(() => confirmSelection());
    if (confirmedSelection) {
      scrollToSelection(confirmedSelection);
      mobilePendingNoteInputRef.current?.focus();
    }
  }, [confirmSelection, scrollToSelection]);
  let shellStyles = desktopShellStyles;
  if (isMobile) {
    shellStyles = mobileShellStyles;
  }
  if (showDesktopNotesColumn) {
    shellStyles = desktopNotesShellStyles;
  }
  const showSelectionButton = Boolean(
    buttonPosition &&
    artifactNotesEnabled &&
    (!isMobile || noteInteractionState.type === 'idle'),
  );
  const showMobilePendingComposer = Boolean(
    !showDesktopNotesColumn &&
    isMobile &&
    artifactNotesEnabled &&
    pendingSelection &&
    noteInteractionState.type === 'add',
  );
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackModalDirection, setFeedbackModalDirection] = useState<Exclude<
    MemberReflectionFeedbackValue,
    null
  > | null>(null);
  const [feedbackModalError, setFeedbackModalError] = useState<string | null>(
    null,
  );

  const handleOpenFeedbackModal = useCallback(
    (direction: Exclude<MemberReflectionFeedbackValue, null>) => {
      setFeedbackModalDirection(direction);
      setFeedbackModalError(null);
      setFeedbackModalOpen(true);
    },
    [],
  );

  const handleCloseFeedbackModal = useCallback(() => {
    setFeedbackModalOpen(false);
    setFeedbackModalError(null);
  }, []);

  const handleSubmitFeedbackModal = useCallback(
    async (feedbackText: string) => {
      if (!feedbackModalDirection) {
        return;
      }
      setFeedbackModalError(null);
      try {
        await onFeedback?.(feedbackModalDirection, feedbackText);
        setFeedbackModalOpen(false);
      } catch {
        setFeedbackModalError(
          'Your feedback could not be submitted. Please try again.',
        );
      }
    },
    [feedbackModalDirection, onFeedback],
  );

  const headerActions = (
    <HeaderActions
      copyAriaLabel={copyAriaLabel}
      copyState={copyState}
      copyTooltipLabel={copyTooltipLabel}
      feedbackValue={isReady ? feedbackValue : null}
      isFeedbackSubmitting={isFeedbackSubmitting}
      onCopy={onCopy}
      onFeedbackButtonClick={isReady ? handleOpenFeedbackModal : undefined}
    />
  );
  let introCallout: ReactNode = null;
  if (viewerContext === 'member' && !introCopy) {
    if (showIntroCallout) {
      introCallout = (
        <ReflectionOnboardingCallout
          dismissError={introDismissError}
          isDismissing={isDismissingIntro}
          onDismiss={onDismissIntro}
        />
      );
    }
  } else {
    introCallout = (
      <section style={calloutStyles}>
        <p
          style={{
            color: colors.body,
            fontFamily: font.sans,
            fontSize: 14,
            lineHeight: 1.6,
            margin: 0,
          }}
        >
          {introCopy || COACH_INTRO_COPY}
        </p>
      </section>
    );
  }

  return (
    <div style={pageStyles}>
      <FeedbackModal
        direction={feedbackModalDirection}
        error={feedbackModalError}
        isSubmitting={isFeedbackSubmitting}
        open={feedbackModalOpen}
        onClose={handleCloseFeedbackModal}
        onSubmit={handleSubmitFeedbackModal}
      />
      <ReflectionPromptReview
        canResume={promptSuspended && keepEnabled}
        draft={promptDraft ?? ''}
        enabled={keepEnabled && !promptSuspended}
        open={promptDraft !== null}
        onChange={setPromptDraft}
        onDiscard={() => setPromptDraft(null)}
        onResume={() => setPromptSuspended(false)}
      />
      {showSelectionButton && buttonPosition ? (
        <ArtifactSelectionButton
          initialPosition={buttonPosition}
          isMobile={isMobile}
          label={keepEnabled ? 'Keep selection' : 'Add note'}
          range={selectionRange}
          onConfirm={handleConfirmTextSelection}
        />
      ) : null}
      <header>
        <div style={topBarStyles}>
          <div style={topBarLeftStyles}>
            <IconButton aria-label="Back" size="small" onClick={onBack}>
              <Icon fontSize="small">arrow_back</Icon>
            </IconButton>
            {!isMobile ? (
              <HeaderMetadata
                artifactTitle={artifactTitle}
                generationDateLabel={generationDateLabel}
                isMobile={false}
                participantLabel={participantLabel}
                sessionDateLabel={sessionDateLabel}
                viewerContext={viewerContext}
              />
            ) : null}
          </div>
          {reflectionKeep &&
          artifactNotesEnabled &&
          showSavedNotesLink &&
          viewerContext === 'member' ? (
            <a
              href="/m/highlights?view=owned-snapshots"
              style={{ color: colors.sage, fontSize: 13 }}
              onClick={
                onManageSavedNotes
                  ? (event) => {
                      event.preventDefault();
                      onManageSavedNotes();
                    }
                  : undefined
              }
            >
              Saved notes
            </a>
          ) : null}
          {headerActions}
        </div>
        {isMobile ? (
          <HeaderMetadata
            artifactTitle={artifactTitle}
            generationDateLabel={generationDateLabel}
            participantLabel={participantLabel}
            sessionDateLabel={sessionDateLabel}
            viewerContext={viewerContext}
            isMobile
          />
        ) : null}
      </header>

      {newKeepDraftSuspended ? (
        <aside
          className="fs-mask"
          data-dd-privacy="mask"
          data-reflection-control="true"
        >
          <Alert severity="info">
            This Keep draft is suspended while reflection actions are
            unavailable. It was not converted to a legacy note.
          </Alert>
          <Button
            disabled={!keepEnabled}
            onClick={() => setKeepSuspended(false)}
          >
            Resume Keep draft
          </Button>
          <Button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(pendingNoteDraft);
              } catch {
                setPendingNoteError('Couldn’t copy your draft.');
              }
            }}
          >
            Copy own draft
          </Button>
          <Button onClick={handleCancelPendingNote}>Discard Keep draft</Button>
        </aside>
      ) : null}
      <div data-testid="artifact-notes-layout-shell" style={shellStyles}>
        <main
          ref={contentRef}
          className={reflectionKeep ? 'fs-mask' : undefined}
          data-dd-privacy={reflectionKeep ? 'mask' : undefined}
          style={contentStyles}
          onClickCapture={handleMobilePendingNoteTapOutside}
        >
          {!isReady
            ? renderStatus({ errorAction, errorMessage, status, statusCopy })
            : null}

          {isReady ? (
            <>
              {introCallout}

              {sourceReflectionLink ? (
                <RouterLink
                  className="mb-6 inline-flex items-center rounded-lg text-sm font-bold text-sage-600 no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-500"
                  to={sourceReflectionLink.href}
                >
                  {sourceReflectionLink.label} <span aria-hidden="true">›</span>
                </RouterLink>
              ) : null}

              {markdown ? (
                <ReflectionContent
                  activeHighlightId={activeScrolledHighlightId}
                  highlights={displayHighlights}
                  markdown={markdown}
                  onHighlightClick={
                    isMobile ? handleMobileHighlightClick : undefined
                  }
                  onKeep={keepEnabled ? handleWholeBlockKeep : undefined}
                  onUse={keepEnabled ? handleWholeBlockUse : undefined}
                />
              ) : (
                <Typography color="text.secondary" variant="body2">
                  {contentUnavailableMessage}
                </Typography>
              )}

              {!showDesktopNotesColumn &&
              !isMobile &&
              artifactNotesEnabled &&
              pendingSelection ? (
                <>
                  {pendingNoteError ? (
                    <Alert severity="error" style={{ marginTop: 16 }}>
                      {pendingNoteError}
                    </Alert>
                  ) : null}
                  <PendingArtifactNoteComposer
                    key={`${pendingSelection.blockId}:${pendingSelection.startIndex}:${pendingSelection.endIndex}`}
                    disabled={!onArtifactNoteCreate || newKeepDraftSuspended}
                    note={pendingNoteDraft}
                    onCancel={handleCancelPendingNote}
                    onNoteChange={handlePendingNoteDraftChange}
                    onSave={handleSavePendingNote}
                  />
                </>
              ) : null}

              {showMobilePendingComposer && pendingSelection ? (
                <div
                  ref={mobilePendingNoteDockRef}
                  style={{
                    ...mobilePendingNoteDockStyles,
                    bottom: mobileViewportBottomInset,
                  }}
                >
                  {pendingNoteError ? (
                    <Alert severity="error" style={{ marginBottom: 12 }}>
                      {pendingNoteError}
                    </Alert>
                  ) : null}
                  <PendingArtifactNoteComposer
                    key={`${pendingSelection.blockId}:${pendingSelection.startIndex}:${pendingSelection.endIndex}`}
                    disabled={!onArtifactNoteCreate || newKeepDraftSuspended}
                    note={pendingNoteDraft}
                    style={{ margin: 0 }}
                    variant="mobileDock"
                    autoFocus
                    onCancel={handleMobilePendingNoteCancel}
                    onNoteChange={handlePendingNoteDraftChange}
                    onSave={handleSavePendingNote}
                    onTextAreaReady={(element) => {
                      mobilePendingNoteInputRef.current = element;
                    }}
                  />
                </div>
              ) : null}

              {transcriptHref ? (
                <div style={transcriptFooterStyles}>
                  <RouterLink style={transcriptLinkStyles} to={transcriptHref}>
                    View transcript
                  </RouterLink>
                </div>
              ) : null}

              {coachSelectionCard ? (
                <section
                  aria-labelledby="coach-selection-card-title"
                  style={coachSelectionCardStyles}
                >
                  <div>
                    <Typography
                      component="h2"
                      id="coach-selection-card-title"
                      style={{ color: colors.heading, fontFamily: font.sans }}
                      variant="h5"
                    >
                      Want {coachSelectionCard.coachFirstName} to be your coach?
                    </Typography>
                    <Typography
                      component="p"
                      style={{
                        color: colors.muted,
                        fontFamily: font.sans,
                        margin: '14px 0 12px',
                      }}
                      variant="body1"
                    >
                      A few questions to help you decide:
                    </Typography>
                    <ol
                      style={{
                        color: colors.muted,
                        fontFamily: font.sans,
                        fontSize: 16,
                        letterSpacing: '0.15px',
                        lineHeight: 1.5,
                        margin: 0,
                        paddingLeft: 24,
                      }}
                    >
                      <li>
                        Did {coachSelectionCard.coachFirstName} help you see
                        something in a new way?
                      </li>
                      <li>
                        Did you feel comfortable being yourself during the call?
                      </li>
                      <li>
                        Did what {coachSelectionCard.coachFirstName} offered
                        feel useful?
                      </li>
                    </ol>
                  </div>
                  <RouterButton
                    component={RouterLink}
                    to={coachSelectionCard.href}
                    variant="contained"
                  >
                    Book with {coachSelectionCard.coachFirstName}
                  </RouterButton>
                  <Typography
                    component="p"
                    style={{
                      color: colors.muted,
                      fontFamily: font.sans,
                      margin: 0,
                    }}
                    variant="body2"
                  >
                    You’ll choose a recurring time for your biweekly sessions.
                    Once you book, {coachSelectionCard.coachFirstName} will
                    become your coach, and we’ll close things out with any other
                    coaches you were considering.
                  </Typography>
                </section>
              ) : null}

              {survey}

              {!showDesktopNotesColumn && artifactNotesEnabled && !isMobile ? (
                <SavedArtifactNotesList
                  author={artifactNoteAuthor}
                  editDraft={activeEditDraft}
                  editingNoteId={activeEditNote?.id ?? null}
                  isBusy={savedNoteMutationIsPending}
                  notes={savedNotes}
                  onCancelEdit={handleCancelDesktopEditArtifactNote}
                  onDelete={handleDeleteArtifactNote}
                  onEditDraftChange={handleEditDraftChange}
                  onEditSaved={setIdleNoteInteraction}
                  onStartEdit={handleStartEditArtifactNote}
                  onUpdate={onArtifactNoteUpdate}
                />
              ) : null}
            </>
          ) : null}
        </main>
        {isMobile &&
        (noteInteractionState.type === 'view' ||
          noteInteractionState.type === 'edit') ? (
          <MobileArtifactNoteViewer
            activeNoteId={noteInteractionState.activeNoteId}
            author={artifactNoteAuthor}
            deleteDisabled={savedNoteMutationIsPending}
            disabled={savedNoteMutationIsPending}
            draft={
              noteInteractionState.type === 'edit'
                ? noteInteractionState.draft
                : ''
            }
            isEditing={noteInteractionState.type === 'edit'}
            isMenuOpen={
              noteInteractionState.type === 'view'
                ? noteInteractionState.isMenuOpen
                : false
            }
            notes={savedNotes}
            onActiveNoteMissing={setIdleNoteInteraction}
            onCancelEdit={handleCancelEditArtifactNote}
            onClose={handleCloseMobileNoteViewer}
            onDelete={handleDeleteArtifactNote}
            onDraftChange={handleEditDraftChange}
            onMenuOpenChange={handleMobileMenuOpenChange}
            onNavigate={handleNavigateMobileNoteViewer}
            onSaveEdit={handleSaveEditArtifactNote}
            onStartEdit={handleStartEditArtifactNote}
          />
        ) : null}
        {showDesktopNotesColumn ? (
          <aside
            aria-label="Artifact notes side column"
            data-testid="artifact-notes-side-column"
            style={{
              ...desktopNotesColumnStyles,
              minHeight: desktopNotesColumnMinHeight,
            }}
          >
            {pendingSelection ? (
              <div
                style={{
                  position: 'absolute',
                  top: cardTops.get('pending-artifact-note') ?? 0,
                  width: '100%',
                  zIndex: 2,
                }}
              >
                {pendingNoteError ? (
                  <Alert severity="error" style={{ marginBottom: 12 }}>
                    {pendingNoteError}
                  </Alert>
                ) : null}
                <PendingArtifactNoteComposer
                  key={`${pendingSelection.blockId}:${pendingSelection.startIndex}:${pendingSelection.endIndex}`}
                  disabled={!onArtifactNoteCreate || newKeepDraftSuspended}
                  note={pendingNoteDraft}
                  style={{ margin: 0 }}
                  onCancel={handleCancelPendingNote}
                  onNoteChange={handlePendingNoteDraftChange}
                  onSave={handleSavePendingNote}
                />
              </div>
            ) : null}
            <SavedArtifactNotesList
              author={artifactNoteAuthor}
              cardTops={cardTops}
              editDraft={activeEditDraft}
              editingNoteId={activeEditNote?.id ?? null}
              isBusy={savedNoteMutationIsPending}
              measuredCardHeights={measuredCardHeights}
              notes={savedNotes}
              positioned
              onCancelEdit={handleCancelDesktopEditArtifactNote}
              onDelete={handleDeleteArtifactNote}
              onEditDraftChange={handleEditDraftChange}
              onEditSaved={setIdleNoteInteraction}
              onHeightChange={handleCardHeightChange}
              onStartEdit={handleStartEditArtifactNote}
              onUpdate={onArtifactNoteUpdate}
            />
          </aside>
        ) : null}
      </div>
    </div>
  );
};

export default MemberReflectionArtifactView;
